import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchOrder, fetchUnshippedOrders } from '../../api/orders';
import ConfirmShipmentModal from './ConfirmShipmentModal';
import BulkConfirmShipmentModal from './BulkConfirmShipmentModal';
import RenameBatchModal from './RenameBatchModal';
import RemarkHistoryModal, { RemarkHistoryButton } from './RemarkHistoryModal';
import {
  createProcurementBatch,
  clearProcurementBatches,
  deleteProcurementBatch,
  fetchProcurementBatches,
  fetchProcurementBatchSummaries,
  setProcurementLineConfirmed,
  updateProcurementBatch,
  updateProcurementStatus,
  lookupHawbByTracking,
} from '../../api/procurement';
import {
  EmptyState,
  PageHeader,
  StatusBanner,
  PageLoading,
  showStatusToast,
  inputClassName,
  labelClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  ConfirmDialog,
} from '../../components/ui';
import { ProductThumb } from '../../components/ui/ProductThumb';
import { CopyableValue } from '../../components/ui/CopyableValue';
import { fetchProducts } from '../../api/products';
import { fetchInventory } from '../../api/inventory';

import { loadProcurementDraft, clearProcurementDraft } from '../orders/procurementDraft';
import { addPriorityOrderId } from '../customer-service/priorityOrdersStorage';
import {
  BUY_MARKETPLACES,
  amazonAsinUrl,
  formatBuyMoney,
  getBuyMarketplace,
} from './buyMarketplaces';

function toDateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

// Beautiful badge for status of procurement batch
function BatchStatusBadge({ status }) {
  const statusStyles = {
    draft: {
      dot: 'bg-amber-500',
      text: 'text-amber-800',
      label: 'draft',
    },
    ordered: {
      dot: 'bg-emerald-500',
      text: 'text-emerald-800',
      label: 'Ordered',
    },
    shipped: {
      dot: 'bg-blue-500',
      text: 'text-blue-800',
      label: 'shipped',
    },
    received: {
      dot: 'bg-emerald-500',
      text: 'text-emerald-800',
      label: 'received',
    },
    cancelled: {
      dot: 'bg-red-500',
      text: 'text-red-800',
      label: 'cancelled',
    },
  };

  const style = statusStyles[status] || statusStyles.draft;

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold select-none">
      <span className={`h-2 w-2 rounded-full ${style.dot}`} />
      <span className={style.text}>{style.label}</span>
    </span>
  );
}

// Beautiful badge for Custom batch indicator
function CustomBatchBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold select-none">
      <span className="h-2 w-2 rounded-full bg-blue-500" />
      <span className="text-blue-800">Custom</span>
    </span>
  );
}

function ReorderBadge() {
  return (
    <span className="inline-flex items-center rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 ring-1 ring-inset ring-violet-200">
      Reorder
    </span>
  );
}

/** Fixed-width autosave chip — never shifts the batch title while saving. */
function QuietSavePill({ state }) {
  const label =
    state === 'pending'
      ? 'Pending…'
      : state === 'saving'
        ? 'Saving…'
        : state === 'saved'
          ? 'Saved'
          : state === 'error'
            ? 'Save failed'
            : '';
  const tone =
    state === 'saved'
      ? 'text-emerald-700 bg-emerald-50 ring-emerald-200'
      : state === 'error'
        ? 'text-red-700 bg-red-50 ring-red-200'
        : 'text-slate-600 bg-slate-100 ring-slate-200';

  return (
    <span
      className={`inline-flex h-5 min-w-[4.75rem] items-center justify-center rounded-md px-1.5 text-[10px] font-semibold tracking-wide ring-1 ring-inset transition-opacity duration-200 ${
        state ? `${tone} opacity-100` : 'opacity-0'
      }`}
      aria-live="polite"
    >
      {label || '\u00a0'}
    </span>
  );
}

const INLINE_FIELD_CLASS =
  'border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-700 bg-white transition-[border-color,box-shadow,background-color] duration-150 ease-out hover:border-slate-300 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/15 disabled:opacity-65 disabled:bg-slate-50 disabled:cursor-not-allowed';

const PROCUREMENT_TH_CLASS =
  'sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap backdrop-blur-sm shadow-[inset_0_-1px_0_0_rgb(226,232,240)]';

/** When nested table scroll hits top/bottom, continue scrolling the page. */
function chainWheelToPageScroll(event) {
  const node = event.currentTarget;
  const delta = event.deltaY;
  if (!delta) return;

  const { scrollTop, scrollHeight, clientHeight } = node;
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  const atTop = scrollTop <= 0;
  const atBottom = scrollTop >= maxScroll - 1;

  if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
    const page = document.querySelector('[data-page-scroll="true"]');
    if (!page) return;
    event.preventDefault();
    page.scrollTop += delta;
  }
}

function ProcurementPage({ token }) {
  const [searchParams] = useSearchParams();
  const [batches, setBatches] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchPagination, setBatchPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [batchDays, setBatchDays] = useState('90');
  const [batchStatus, setBatchStatus] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [draftLines, setDraftLines] = useState([]);
  const [status, setStatus] = useState({ type: 'loading', message: '' });
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [clearing, setClearing] = useState(false);

  const [editingBatch, setEditingBatch] = useState(null);
  const [editingStatus, setEditingStatus] = useState('draft');
  const [editingName, setEditingName] = useState('');
  const [editingLines, setEditingLines] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  // Small caches only — never load the full catalog / inventory into memory.
  const [productsList, setProductsList] = useState([]);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [inventoryList, setInventoryList] = useState([]);
  const [draftBatchName, setDraftBatchName] = useState('');
  const [focusedLineIndex, setFocusedLineIndex] = useState(null);
  const [focusedLineIsDraft, setFocusedLineIsDraft] = useState(true);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [confirmNotFeasibleLine, setConfirmNotFeasibleLine] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    isOpen: false,
    title: '',
    message: '',
    tone: 'warning',
  });
  const [cancelBatchId, setCancelBatchId] = useState(null);
  const [renameBatchTarget, setRenameBatchTarget] = useState(null);
  const [renamingBatch, setRenamingBatch] = useState(false);
  const [confirmShipmentTarget, setConfirmShipmentTarget] = useState(null);
  const [bulkConfirmTargets, setBulkConfirmTargets] = useState(null);
  const [inlineEditingBatchId, setInlineEditingBatchId] = useState(null);
  const [inlineEditBackup, setInlineEditBackup] = useState(null);
  const [inlineEditStartedAt, setInlineEditStartedAt] = useState(null);
  const [savingInlineEdit, setSavingInlineEdit] = useState(false);
  const [bulkHawbInput, setBulkHawbInput] = useState('');
  const [bulkUsOrderId, setBulkUsOrderId] = useState('');
  const [bulkUsTrackingId, setBulkUsTrackingId] = useState('');
  const [selectedBulkLineIds, setSelectedBulkLineIds] = useState(() => new Set());
  const [hawbLookupLineIds, setHawbLookupLineIds] = useState(() => new Set());
  const [remarkHistoryTarget, setRemarkHistoryTarget] = useState(null);
  /** 'lines' = one row per order line; 'product' = group same SKU/ASIN with total qty */
  const [batchLinesView, setBatchLinesView] = useState('product');
  /** Product groups start collapsed; keys here are expanded (orders visible). */
  const [expandedProductKeys, setExpandedProductKeys] = useState(() => new Set());
  /** Quiet autosave feedback per batch: '' | pending | saving | saved | error */
  const [quietSaveByBatch, setQuietSaveByBatch] = useState({});
  const activeRequests = useRef({});
  const batchesRef = useRef([]);
  const inventoryListRef = useRef(inventoryList);
  const inlineEditingBatchIdRef = useRef(null);
  const batchLoadSeq = useRef(0);
  const selectedBatchIdRef = useRef(null);
  const hawbLookupSeq = useRef(0);
  /** Cache HAWB by normalized US tracking so identical trackings skip repeat lookups. */
  const hawbByTrackingCache = useRef(new Map());
  const inlineSaveTimers = useRef({});
  const quietSaveClearTimers = useRef({});

  const normalizeTrackingKey = (tracking) => String(tracking || '').trim().toUpperCase();

  const rememberHawbForTracking = (tracking, hawb) => {
    const key = normalizeTrackingKey(tracking);
    const value = String(hawb || '').trim();
    if (!key || !value) return;
    hawbByTrackingCache.current.set(key, value);
  };

  const findCachedHawbForTracking = (batchId, tracking) => {
    const key = normalizeTrackingKey(tracking);
    if (!key) return '';

    const cached = hawbByTrackingCache.current.get(key);
    if (cached) return cached;

    const batch = batchesRef.current.find((entry) => entry.id === batchId);
    const fromBatch = (batch?.lines || []).find(
      (line) =>
        normalizeTrackingKey(line.usTrackingId) === key && String(line.hawb || '').trim()
    );
    const hawb = String(fromBatch?.hawb || '').trim();
    if (hawb) {
      hawbByTrackingCache.current.set(key, hawb);
    }
    return hawb;
  };

  const applyHawbToMatchingTracking = (batchId, tracking, hawb, { onlyLineIds = null } = {}) => {
    const key = normalizeTrackingKey(tracking);
    const value = String(hawb || '').trim();
    if (!key || !value) return 0;

    rememberHawbForTracking(tracking, value);

    let updatedCount = 0;
    let updatedBatchLines = [];
    setBatches((current) =>
      current.map((batch) => {
        if (batch.id !== batchId) return batch;
        updatedBatchLines = batch.lines.map((line) => {
          if (normalizeTrackingKey(line.usTrackingId) !== key) return line;
          if (onlyLineIds && !onlyLineIds.has(line.id)) return line;
          if (String(line.hawb || '').trim() === value) return line;
          updatedCount += 1;
          return { ...line, hawb: value };
        });
        return { ...batch, lines: updatedBatchLines };
      })
    );
    batchesRef.current = batchesRef.current.map((batch) => {
      if (batch.id !== batchId) return batch;
      return { ...batch, lines: updatedBatchLines };
    });
    return updatedCount;
  };

  useEffect(() => {
    inventoryListRef.current = inventoryList;
  }, [inventoryList]);

  useEffect(() => {
    inlineEditingBatchIdRef.current = inlineEditingBatchId;
  }, [inlineEditingBatchId]);

  useEffect(() => {
    batchesRef.current = batches;
    for (const batch of batches || []) {
      for (const line of batch.lines || []) {
        const tracking = normalizeTrackingKey(line.usTrackingId);
        const hawb = String(line.hawb || '').trim();
        if (tracking && hawb && !hawbByTrackingCache.current.has(tracking)) {
          hawbByTrackingCache.current.set(tracking, hawb);
        }
      }
    }
  }, [batches]);

  useEffect(() => {
    if (!batches?.length) return;
    // Re-sync confirmed Customer Service lines so priority survives reloads.
    for (const batch of batches) {
      for (const line of batch.lines) {
        if (getUIStatus(line.inventoryStatus) === 'Customer Service' && line.confirmed) {
          addPriorityOrderId(line.orderItem?.order?.id);
        }
      }
    }
  }, [batches]);

  const mergeProducts = useCallback((items) => {
    if (!items?.length) {
      return;
    }
    setProductsList((current) => {
      const byId = new Map(current.map((product) => [product.id, product]));
      for (const item of items) {
        byId.set(item.id, item);
      }
      return Array.from(byId.values());
    });
  }, []);

  const pickerProducts = useMemo(() => {
    const source = productSuggestions.length ? productSuggestions : productsList;
    return [...source].sort((a, b) => {
      const titleA = a.title || '';
      const titleB = b.title || '';
      return titleA.localeCompare(titleB);
    });
  }, [productSuggestions, productsList]);

  const collectLineKeys = useCallback((lines = []) => {
    const skus = [];
    const asins = [];
    for (const line of lines) {
      const sku = line.sku || line.orderItem?.sku;
      const asin = line.asin || line.orderItem?.asinIn || line.orderItem?.asinCom;
      if (sku) skus.push(String(sku).trim());
      if (asin) asins.push(String(asin).trim().toUpperCase());
    }
    return {
      skus: [...new Set(skus)].slice(0, 100),
      asins: [...new Set(asins)].slice(0, 100),
    };
  }, []);

  const loadLookupsForKeys = useCallback(
    async (keys) => {
      const skus = keys?.skus || [];
      const asins = keys?.asins || [];
      if (!skus.length && !asins.length) {
        return;
      }

      try {
        const [productsPayload, inventoryPayload] = await Promise.all([
          fetchProducts(token, {
            skus: skus.join(','),
            asins: asins.join(','),
          }),
          fetchInventory(token, {
            skus: skus.join(','),
            asins: asins.join(','),
          }),
        ]);
        mergeProducts(productsPayload.items || []);
        setInventoryList((current) => {
          const byId = new Map(current.map((item) => [item.id, item]));
          for (const item of inventoryPayload.items || []) {
            byId.set(item.id, item);
          }
          return Array.from(byId.values());
        });
      } catch (error) {
        console.error('Failed to load procurement lookups:', error);
      }
    },
    [token, mergeProducts]
  );

  const batchFilterParams = useCallback(() => {
    const params = {
      days: batchDays || '90',
    };
    if (batchStatus) params.status = batchStatus;
    return params;
  }, [batchDays, batchStatus]);

  const loadBatchDetail = useCallback(
    async (batchId) => {
      if (!batchId) {
        setBatches([]);
        batchesRef.current = [];
        return null;
      }
      const seq = ++batchLoadSeq.current;
      const payload = await fetchProcurementBatches(token, {
        batchId,
        page: '1',
        pageSize: '1',
      });
      if (seq !== batchLoadSeq.current) return payload.batches?.[0] || null;
      if (inlineEditingBatchIdRef.current === batchId) {
        return payload.batches?.[0] || null;
      }
      const nextBatches = payload.batches || [];
      setBatches(nextBatches);
      batchesRef.current = nextBatches;
      return nextBatches[0] || null;
    },
    [token]
  );

  const loadBatches = useCallback(
    async (preferId = null) => {
      setStatus({ type: 'loading', message: '' });

      try {
        const payload = await fetchProcurementBatchSummaries(token, batchFilterParams());
        const options = (payload.batches || []).map((batch) =>
          normalizeBatchOption(batch, inventoryListRef.current)
        );
        setBatchOptions(options);
        setBatchPagination({
          page: 1,
          pageSize: options.length || 20,
          total: payload.total ?? options.length,
          totalPages: options.length ? 1 : 0,
        });

        const requestedId = preferId || selectedBatchIdRef.current || searchParams.get('batch');
        const inList = requestedId && options.some((batch) => batch.id === requestedId);
        const nextId = (inList && requestedId) || options[0]?.id || requestedId || null;

        selectedBatchIdRef.current = nextId;
        setSelectedBatchId(nextId);
        const detail = await loadBatchDetail(nextId);
        if (detail) {
          const detailOption = normalizeBatchOption(detail, inventoryListRef.current);
          setBatchOptions((current) => {
            if (current.some((batch) => batch.id === detail.id)) {
              return current.map((batch) =>
                batch.id === detail.id ? { ...batch, ...detailOption } : batch
              );
            }
            return [detailOption, ...current];
          });
          setBatchPagination((current) => {
            if (options.some((batch) => batch.id === detail.id)) return current;
            return {
              page: 1,
              pageSize: options.length + 1,
              total: (payload.total ?? options.length) + 1,
              totalPages: 1,
            };
          });
        }
        setStatus({ type: 'ready', message: '' });
      } catch (error) {
        setStatus({ type: 'error', message: error.message });
      }
    },
    [token, batchFilterParams, loadBatchDetail, searchParams]
  );

  const selectedBatch = useMemo(
    () => batches.find((batch) => batch.id === selectedBatchId) || null,
    [batches, selectedBatchId]
  );

  const activeLookupKey = useMemo(() => {
    const batchKeys = collectLineKeys(
      (batches || []).flatMap((batch) => batch.lines || [])
    );
    const draftKeys = collectLineKeys(draftLines);
    const editKeys = collectLineKeys(editingLines);
    const skus = [...new Set([...batchKeys.skus, ...draftKeys.skus, ...editKeys.skus])]
      .slice(0, 100)
      .sort();
    const asins = [...new Set([...batchKeys.asins, ...draftKeys.asins, ...editKeys.asins])]
      .slice(0, 100)
      .sort();
    return `${skus.join('|')}::${asins.join('|')}`;
  }, [batches, draftLines, editingLines, collectLineKeys]);

  const loadInventory = useCallback(async () => {
    const [skuPart, asinPart] = activeLookupKey.split('::');
    await loadLookupsForKeys({
      skus: skuPart ? skuPart.split('|').filter(Boolean) : [],
      asins: asinPart ? asinPart.split('|').filter(Boolean) : [],
    });
  }, [activeLookupKey, loadLookupsForKeys]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    if (!token) {
      return;
    }
    loadInventory();
  }, [token, loadInventory]);

  // Server-side product search for the focused picker (never filter 1 lakh rows client-side).
  useEffect(() => {
    if (focusedLineIndex == null) {
      return undefined;
    }

    const lines = focusedLineIsDraft ? draftLines : editingLines;
    const line = lines[focusedLineIndex];
    const term = (line?.productSearchTerm || '').trim();

    if (term.length < 2) {
      setProductSuggestions([]);
      setProductSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProductSearchLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const payload = await fetchProducts(token, {
          search: term,
          page: '1',
          pageSize: '25',
        });
        if (cancelled) {
          return;
        }
        const items = payload.items || [];
        setProductSuggestions(items);
        mergeProducts(items);
      } catch (error) {
        if (!cancelled) {
          console.error('Product search failed:', error);
          setProductSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setProductSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    focusedLineIndex,
    focusedLineIsDraft,
    draftLines,
    editingLines,
    token,
    mergeProducts,
  ]);

  useEffect(() => {
    if (searchParams.get('from') !== 'orders') {
      return;
    }

    const draft = loadProcurementDraft();
    if (!draft.length) {
      return;
    }

    // Build the draft UI immediately from sessionStorage — no per-line fetchOrder round trips.
    // Previously each line awaited fetchOrder sequentially (often 10+ seconds).
    const hydrated = [];
    for (const line of draft) {
      const orderSnapshot = line.order || line.orderItem?.order || null;
      const selectedItem = line.orderItem
        ? { ...line.orderItem, order: orderSnapshot || line.orderItem.order || undefined }
        : null;

      if (!selectedItem?.id && !line.orderItemId) {
        console.error('Skipping procurement draft line with missing order item', line);
        continue;
      }

      const orderItemId = selectedItem?.id || line.orderItemId;
      const orderItem = selectedItem
        ? { ...selectedItem, id: orderItemId, order: orderSnapshot || selectedItem.order }
        : {
            id: orderItemId,
            order: orderSnapshot || undefined,
            quantityOrdered: line.quantity || 1,
            title: line.title || null,
            sku: line.sku || null,
            asinIn: line.asinIn || null,
            asinCom: line.asinCom || null,
          };

      hydrated.push({
        orderId: orderSnapshot?.id || line.orderId || null,
        orderItemId,
        orderItem,
        productLink: line.productLink || '',
        quantity: orderItem.quantityOrdered || line.quantity || 1,
        extraQuantity: line.extraQuantity || 0,
        supplierNotes: line.supplierNotes || '',
        isReorder: Boolean(line.isReorder),
        label: `${orderSnapshot?.amazonOrderId || line.amazonOrderId || 'Order'} · ${
          orderSnapshot?.buyerName || line.buyerName || 'Buyer'
        }`,
      });
    }

    setDraftLines(hydrated);
    clearProcurementDraft();

    // Background refresh only for lines missing a full order snapshot (unique IDs, in parallel).
    const missingOrderIds = [
      ...new Set(
        hydrated
          .filter((line) => line.orderId && !line.orderItem?.order?.amazonOrderId)
          .map((line) => line.orderId)
      ),
    ];

    if (!missingOrderIds.length) {
      return;
    }

    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        missingOrderIds.map((orderId) => fetchOrder(token, orderId))
      );
      if (cancelled) return;

      const orderById = new Map();
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value?.order) {
          orderById.set(missingOrderIds[index], result.value.order);
        }
      });

      if (!orderById.size) return;

      setDraftLines((current) =>
        current.map((line) => {
          const order = orderById.get(line.orderId);
          if (!order) return line;
          const items = order.items || [];
          let selectedItem = items.find((item) => item.id === line.orderItemId);
          if (!selectedItem && items.length > 0) {
            const draftAsin = line.orderItem?.asinIn || line.orderItem?.asinCom;
            const draftSku = line.orderItem?.sku;
            selectedItem = items.find((item) => {
              if (draftSku && item.sku && item.sku === draftSku) return true;
              const itemAsin = item.asinIn || item.asinCom;
              return Boolean(draftAsin && itemAsin && itemAsin === draftAsin);
            });
          }
          if (!selectedItem?.id) return line;
          selectedItem = { ...selectedItem, order };
          return {
            ...line,
            orderItemId: selectedItem.id,
            orderItem: selectedItem,
            quantity: selectedItem.quantityOrdered || line.quantity || 1,
            label: `${order.amazonOrderId || 'Order'} · ${order.buyerName || 'Buyer'}`,
          };
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, token]);

  const updateDraftLine = (index, field, value) => {
    setDraftLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index ? applyStatusSideEffects(line, field, value, inventoryList) : line
      )
    );
  };

  const startCustomBatch = () => {
    setDraftLines([
      {
        orderItemId: null,
        companyName: '',
        productLink: '',
        quantity: '',
        extraQuantity: 0,
        supplierNotes: '',
        label: 'Custom procurement line',
        isCustom: true,
        selectedProductId: '',
        asin: '',
        sku: '',
        price: '',
        title: '',
        usOrderId: '',
        usTrackingId: '',
        hawb: '',
        sellingPrice: '',
        purchasePrice: '',
        buyMarketplace: 'US',
        buyCarrierName: '',
        inventoryStatus: 'Amazon',
      },
    ]);
    setDraftBatchName(`Custom Batch - ${new Date().toISOString().split('T')[0]}`);
  };

  const addDraftBlankLine = () => {
    setDraftLines((current) => [
      ...current,
      {
        orderItemId: null,
        companyName: '',
        productLink: '',
        quantity: '',
        extraQuantity: 0,
        supplierNotes: '',
        label: 'Custom procurement line',
        isCustom: true,
        selectedProductId: '',
        asin: '',
        sku: '',
        price: '',
        title: '',
        usOrderId: '',
        usTrackingId: '',
        hawb: '',
        sellingPrice: '',
        purchasePrice: '',
        buyMarketplace: 'US',
        buyCarrierName: '',
        inventoryStatus: 'Amazon',
      },
    ]);
  };

  const importUnshipped = async () => {
    setImporting(true);
    setStatus({ type: 'loading', message: 'Importing unshipped orders into draft...' });

    try {
      const payload = await fetchUnshippedOrders(token);
      const orders = payload.orders || [];

      const unshippedItems = [];
      for (const order of orders) {
        const pendingItems = (order.items || []).filter((item) => item.procurementStatus === 'pending');
        if (pendingItems.length > 0) {
          unshippedItems.push({
            order,
            pendingItems,
          });
        }
      }

      if (!unshippedItems.length) {
        setStatus({ type: 'warning', message: 'No new unshipped orders to import.' });
        setImporting(false);
        return;
      }

      const newDraftLines = [];
      for (const group of unshippedItems) {
        for (const item of group.pendingItems) {
          const itemWithOrder = { ...item, order: group.order };
          newDraftLines.push({
            orderItemId: item.id,
            orderItem: itemWithOrder,
            productLink: '',
            quantity: item.quantityOrdered || 1,
            extraQuantity: 0,
            supplierNotes: '',
            label: `${group.order.amazonOrderId} · ${group.order.buyerName || 'Buyer'}`,
            inventoryStatus: 'Amazon',
          });
        }
      }

      setDraftLines((current) => [...current, ...newDraftLines]);
      setDraftBatchName(`Imported Unshipped - ${new Date().toISOString().split('T')[0]}`);
      setStatus({ type: 'success', message: `Imported ${newDraftLines.length} unshipped order line(s) into draft.` });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setImporting(false);
    }
  };

  const createBatch = async () => {
    const lines = draftLines.filter((line) => line.orderItemId || line.selectedProductId);

    if (!lines.length) {
      setStatus({ type: 'error', message: 'Add at least one line with an order item or product.' });
      return;
    }

    // Validate quantities, selection, and status rules
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const title = line.orderItem?.title || line.title || `Line ${i + 1}`;
      if (!line.quantity || Number(line.quantity) < 1) {
        setStatus({ type: 'error', message: `Line ${i + 1} (${title}): Quantity is mandatory and must be at least 1.` });
        return;
      }
      if (!line.orderItemId && !line.selectedProductId) {
        setStatus({ type: 'error', message: `Line ${i + 1} (${title}): Product selection is mandatory for custom lines.` });
        return;
      }
      if (!line.orderItemId && (!line.companyName?.trim() || !line.productLink?.trim())) {
        setStatus({
          type: 'error',
          message: `Line ${i + 1} (${title}): Company name and product link are mandatory.`,
        });
        return;
      }

      const statusVal = getUIStatus(line.inventoryStatus);

      // Validation 2: Inventory can only be selected when stock is available
      const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
      const sku = line.orderItem?.sku || line.sku;
      const invItem = inventoryList.find(
        (item) =>
          (sku && item.sku === sku) ||
          (asin && (item.asin === asin || item.asin === line.orderItem?.asinCom || item.asin === line.orderItem?.asinIn))
      );
      const stockVal = invItem ? invItem.quantityOnHand : (line.orderItem?.quantityOnHand || 0);

      if (statusVal === 'Inventory' && stockVal <= 0) {
        setStatus({
          type: 'error',
          message: `Line ${i + 1} (${title}): Status 'Inventory' can only be selected when stock is available.`,
        });
        return;
      }
    }

    setCreating(true);

    try {
      const payload = await createProcurementBatch(
        token,
        lines.map((line) => {
          const { orderItemId, inventoryItemId, companyName, productLink, quantity, extraQuantity, buyQuantity, supplierNotes, sku, asin, title, usOrderId, usTrackingId, hawb, sellingPrice, purchasePrice, purchaseTotal, buyMarketplace, buyCarrierName, inventoryStatus } =
            toProcurementLinePayload(line);
          return {
            orderItemId,
            inventoryItemId,
            companyName,
            productLink,
            quantity,
            extraQuantity,
            buyQuantity,
            supplierNotes,
            sku,
            asin,
            title,
            usOrderId,
            usTrackingId,
            hawb,
            sellingPrice,
            purchasePrice,
            purchaseTotal,
            buyMarketplace,
            buyCarrierName,
            inventoryStatus,
            isReorder: Boolean(line.isReorder),
          };
        }),
        draftBatchName.trim() || null
      );

      const newBatchId = payload?.batch?.id || null;
      const newBatchCode = payload?.batch?.batchCode || payload?.batch?.name || null;

      setDraftLines([]);
      setDraftBatchName('');
      setStatus({
        type: 'success',
        message: newBatchCode
          ? `Procurement batch ${newBatchCode} created.`
          : 'Procurement batch created.',
      });

      selectedBatchIdRef.current = newBatchId;
      setSelectedBatchId(newBatchId);
      await loadBatches(newBatchId);
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setCreating(false);
    }
  };

  const handleClearAllClick = () => {
    setIsClearModalOpen(true);
  };

  const clearAllBatches = async () => {
    setIsClearModalOpen(false);
    setClearing(true);
    setStatus({ type: 'loading', message: 'Clearing procurement batches...' });

    try {
      const payload = await clearProcurementBatches(token);
      setBatches([]);
      setBatchOptions([]);
      selectedBatchIdRef.current = null;
      setSelectedBatchId(null);
      setDraftLines([]);
      setStatus({
        type: 'success',
        message: payload.message || 'Procurement cleared.',
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setClearing(false);
    }
  };

  const advanceStatus = async (batchId, nextStatus) => {
    try {
      await updateProcurementStatus(token, batchId, nextStatus);
      if (nextStatus === 'received') {
        setStatus({
          type: 'success',
          message:
            'Batch marked as received. Custom batch quantities and extra stock were added to inventory; Inventory lines were deducted where applicable.',
        });
        await Promise.all([loadBatches(), loadInventory()]);
      } else {
        setStatus({ type: 'success', message: `Batch marked as ${nextStatus}.` });
        await loadBatches();
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };

  const startBatchEdit = (batch) => {
    setEditingBatch(batch);
    setEditingStatus(batch.status);
    setEditingName(batch.name || '');
    setEditingLines(
      batch.lines.map((line) => {
        const isCustomLine = !line.orderItemId;
        let matchedProductId = line.inventoryItemId || '';
        if (isCustomLine && line.orderItem && !matchedProductId) {
          const matched = productsList.find((p) => p.sellerSku === line.orderItem.sku || p.asin === (line.orderItem.asinIn || line.orderItem.asinCom));
          if (matched) {
            matchedProductId = matched.id;
          }
        }
        return {
          id: line.id,
          orderItemId: line.orderItemId,
          orderItem: line.orderItem,
          companyName: line.companyName || '',
          productLink: line.productLink || '',
          quantity: line.quantity,
          extraQuantity: line.extraQuantity,
          buyQuantity:
            line.buyQuantity != null && Number(line.buyQuantity) > 0
              ? Number(line.buyQuantity)
              : Number(line.quantity || 0) + Number(line.extraQuantity || 0),
          supplierNotes: line.supplierNotes || '',
          label:
            line.orderItem?.order?.amazonOrderId && line.orderItem?.order?.buyerName
              ? `${line.orderItem.order.amazonOrderId} · ${line.orderItem.order.buyerName}`
              : line.orderItem?.title || 'Manual line',
          isCustom: isCustomLine,
          selectedProductId: matchedProductId,
          asin: line.orderItem?.asinIn || line.orderItem?.asinCom || '',
          sku: line.orderItem?.sku || '',
          price: line.orderItem?.unitPrice || '',
          title: line.orderItem?.title || '',
          usOrderId: line.usOrderId || '',
          usTrackingId: line.usTrackingId || '',
          hawb: line.hawb || '',
          sellingPrice: line.sellingPrice || '',
          purchasePrice: line.purchasePrice || '',
          buyMarketplace: line.buyMarketplace || 'US',
          buyCarrierName: line.buyCarrierName || '',
          inventoryStatus: line.inventoryStatus || 'Amazon',
          confirmed: line.confirmed || false,
          isReorder: Boolean(line.isReorder),
        };
      })
    );
  };

  const closeBatchEdit = () => {
    setEditingBatch(null);
    setEditingStatus('draft');
    setEditingName('');
    setEditingLines([]);
  };

  const addEditLine = () => {
    setEditingLines((current) => [
      ...current,
      {
        id: null,
        orderItemId: null,
        companyName: '',
        productLink: '',
        quantity: '',
        extraQuantity: 0,
        supplierNotes: '',
        label: 'Custom procurement line',
        isCustom: true,
        selectedProductId: '',
        asin: '',
        sku: '',
        price: '',
        title: '',
        usOrderId: '',
        usTrackingId: '',
        hawb: '',
        sellingPrice: '',
        purchasePrice: '',
        buyMarketplace: 'US',
        buyCarrierName: '',
        inventoryStatus: 'Amazon',
      },
    ]);
  };

  const updateEditLine = (index, field, value) => {
    setEditingLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index ? applyStatusSideEffects(line, field, value, inventoryList) : line
      )
    );
  };

  const handleInlineChange = (batchId, lineId, field, value) => {
    setBatches((current) => {
      const next = current.map((batch) => {
        if (batch.id !== batchId) return batch;
        const newLines = batch.lines.map((line) => {
          if (line.id !== lineId) return line;
          const updatedLine = {
            ...line,
            [field]: value,
            ...(field === 'hawb' ? {} : { confirmed: false }),
          };
          const nextLine = applyStatusSideEffects(updatedLine, field, value, inventoryList);
          if (field === 'quantity' || field === 'extraQuantity') {
            const qty = Number(field === 'quantity' ? value : nextLine.quantity || 0);
            const extra = Number(field === 'extraQuantity' ? value : nextLine.extraQuantity || 0);
            nextLine.buyQuantity = Math.max(0, qty) + Math.max(0, extra);
          }
          if (
            field === 'quantity' ||
            field === 'extraQuantity' ||
            field === 'purchasePrice' ||
            field === 'buyQuantity'
          ) {
            nextLine.purchaseTotal = getLinePurchaseTotal(nextLine);
          }
          return nextLine;
        });
        return { ...batch, lines: newLines };
      });
      batchesRef.current = next;
      return next;
    });
  };

  const setLineHawbLookupBusy = (lineId, busy) => {
    setHawbLookupLineIds((current) => {
      const next = new Set(current);
      if (busy) next.add(lineId);
      else next.delete(lineId);
      return next;
    });
  };

  const fetchHawbForTrackingLine = async (batchId, lineId, trackingValue, options = {}) => {
    const { quiet = false, propagate = true } = options;
    const trackingNumber = String(trackingValue || '').trim();
    if (!trackingNumber || trackingNumber.length < 4) {
      return '';
    }

    const existing = findCachedHawbForTracking(batchId, trackingNumber);
    if (existing) {
      if (propagate) {
        applyHawbToMatchingTracking(batchId, trackingNumber, existing);
      } else {
        handleInlineChange(batchId, lineId, 'hawb', existing);
        rememberHawbForTracking(trackingNumber, existing);
      }
      if (!quiet) {
        showStatusToast({
          type: 'success',
          message: `HAWB ${existing} reused for tracking ${trackingNumber}.`,
        });
      }
      return existing;
    }

    const seq = ++hawbLookupSeq.current;
    setLineHawbLookupBusy(lineId, true);

    try {
      const payload = await lookupHawbByTracking(token, trackingNumber);
      if (seq !== hawbLookupSeq.current) return '';

      if (payload?.hawb) {
        const hawb = String(payload.hawb).trim();
        rememberHawbForTracking(trackingNumber, hawb);
        if (propagate) {
          applyHawbToMatchingTracking(batchId, trackingNumber, hawb);
        } else {
          handleInlineChange(batchId, lineId, 'hawb', hawb);
        }
        if (!quiet) {
          showStatusToast({
            type: 'success',
            message: `HAWB ${hawb} filled from UDS for ${trackingNumber}.`,
          });
        }
        return hawb;
      }

      if (!quiet) {
        showStatusToast({
          type: 'warning',
          message: payload?.message || `No HAWB found for ${trackingNumber}.`,
        });
      }
      return '';
    } catch (error) {
      if (seq !== hawbLookupSeq.current) return '';
      if (!quiet) {
        showStatusToast({
          type: 'error',
          message: error.message || 'Failed to look up HAWB from tracking ID.',
        });
      }
      return '';
    } finally {
      setLineHawbLookupBusy(lineId, false);
    }
  };

  const enqueueBatchRequest = async (batchId, fn) => {
    const previousPromise = activeRequests.current[batchId] || Promise.resolve();
    const newPromise = previousPromise.catch(() => { }).then(async () => {
      try {
        await fn();
      } catch (err) {
        console.error("Queue execution error:", err);
        throw err;
      }
    });
    activeRequests.current[batchId] = newPromise;
    return newPromise;
  };

  const isLineConfirmReady = (line) => {
    const order = line.orderItem?.order;
    return Boolean(order?.id && order.trackingDummy && !order.trackingPushed);
  };

  /** Build Amazon confirm targets from lines (dedupe by order). */
  const buildConfirmTargetsFromLines = (lines) => {
    const byOrder = new Map();
    for (const line of lines || []) {
      if (!isLineConfirmReady(line)) continue;
      const order = line.orderItem?.order;
      if (!order?.id || byOrder.has(order.id)) continue;
      byOrder.set(order.id, {
        lineId: line.id,
        orderId: order.id,
        amazonOrderId: order.amazonOrderId || '',
        trackingNumber: order.trackingDummy,
        marketplace: order.marketplace || 'IN',
      });
    }
    return [...byOrder.values()];
  };

  const openBulkConfirmForLines = (lines, { selectIds = true } = {}) => {
    const list = lines || [];
    const readyLines = list.filter(isLineConfirmReady);
    const targets = buildConfirmTargetsFromLines(readyLines);

    if (!targets.length) {
      const withOrder = list.filter((line) => line.orderItem?.order?.id);
      const allShipped =
        withOrder.length > 0 &&
        withOrder.every((line) => line.orderItem?.order?.trackingPushed);
      showStatusToast({
        type: allShipped ? 'success' : 'error',
        message: allShipped
          ? 'All selected orders are already shipped — nothing left to confirm.'
          : 'No orders ready to confirm. Each needs dummy tracking and must not already be shipped.',
      });
      return;
    }

    if (selectIds) {
      setSelectedBulkLineIds(new Set(readyLines.map((line) => line.id)));
    }

    if (targets.length === 1) {
      const only = targets[0];
      const line = readyLines.find((entry) => entry.id === only.lineId);
      const order = line?.orderItem?.order;
      setConfirmShipmentTarget({
        orderId: only.orderId,
        lineId: only.lineId,
        marketplace: only.marketplace,
        trackingNumber: only.trackingNumber,
        amazonOrderId: only.amazonOrderId,
        initialShipDate: toDateInputValue(order?.shipByDate || order?.orderDate),
      });
      return;
    }

    setBulkConfirmTargets(targets);
  };

  /**
   * Confirm this line; if other ready lines share the same SKU/ASIN, confirm those too
   * (Amazon still needs one call per customer order — bulk modal handles that).
   */
  const openConfirmShipment = (batch, line) => {
    const order = line?.orderItem?.order;
    if (!order?.id || !order?.trackingDummy) {
      setAlertInfo({
        isOpen: true,
        title: 'Missing tracking',
        message: 'Dummy tracking is required before confirming shipment.',
        tone: 'warning',
      });
      return;
    }

    const productKey = getProcurementProductKey(line);
    const siblingReady = (batch?.lines || []).filter(
      (candidate) =>
        isLineConfirmReady(candidate) && getProcurementProductKey(candidate) === productKey
    );
    const targetsSource =
      siblingReady.length > 1 ? siblingReady : [line].filter(isLineConfirmReady);

    openBulkConfirmForLines(targetsSource, { selectIds: true });
  };

  const selectConfirmReadyLines = (batch) => {
    const lines = batch.lines || [];
    const readyIds = lines.filter(isLineConfirmReady).map((line) => line.id);
    setSelectedBulkLineIds(new Set(readyIds));

    if (readyIds.length) {
      showStatusToast({
        type: 'success',
        message: `Selected ${readyIds.length} order${readyIds.length === 1 ? '' : 's'} ready to confirm.`,
      });
      return;
    }

    const withOrder = lines.filter((line) => line.orderItem?.order?.id);
    const shippedCount = withOrder.filter((line) => line.orderItem?.order?.trackingPushed).length;
    const missingDummy = withOrder.filter(
      (line) => !line.orderItem?.order?.trackingPushed && !line.orderItem?.order?.trackingDummy
    ).length;

    if (withOrder.length > 0 && shippedCount === withOrder.length) {
      showStatusToast({
        type: 'success',
        message: 'All orders in this batch are already shipped — nothing left to confirm.',
      });
      return;
    }

    if (missingDummy > 0) {
      showStatusToast({
        type: 'warning',
        message: `${missingDummy} order${missingDummy === 1 ? '' : 's'} still need dummy tracking before confirm.`,
      });
      return;
    }

    showStatusToast({
      type: 'warning',
      message: 'No lines ready to confirm.',
    });
  };

  const openBulkConfirmShipment = (batch) => {
    const selectedLines = (batch.lines || []).filter(
      (line) => selectedBulkLineIds.has(line.id) && isLineConfirmReady(line)
    );
    openBulkConfirmForLines(selectedLines, { selectIds: false });
  };

  const toggleProductGroupExpanded = (productKey) => {
    setExpandedProductKeys((current) => {
      const next = new Set(current);
      if (next.has(productKey)) next.delete(productKey);
      else next.add(productKey);
      return next;
    });
  };

  const selectProductGroupLines = (lines) => {
    setSelectedBulkLineIds(new Set((lines || []).map((line) => line.id)));
  };

  /**
   * Set one buy-side field on every eligible line in a product group.
   * Used by the By product group header — fill once, applies to all; child rows stay editable for overrides.
   */
  const setProductGroupBuyField = (batchId, productKey, field, value) => {
    setBatches((current) => {
      const next = current.map((batch) => {
        if (batch.id !== batchId) return batch;
        const newLines = batch.lines.map((line) => {
          if (getProcurementProductKey(line) !== productKey) return line;
          const status = getUIStatus(line.inventoryStatus);
          if (
            field !== 'inventoryStatus' &&
            (status === 'Customer Service' || status === 'Not feasible')
          ) {
            return line;
          }
          const updatedLine = { ...line, [field]: value, confirmed: false };
          const nextLine = applyStatusSideEffects(updatedLine, field, value, inventoryList);
          if (field === 'purchasePrice') {
            nextLine.purchaseTotal = getLinePurchaseTotal(nextLine);
          }
          return nextLine;
        });
        return { ...batch, lines: newLines };
      });
      batchesRef.current = next;
      return next;
    });
  };

  const handleProductGroupBuyFieldChange = (batchId, productKey, field, value) => {
    setProductGroupBuyField(batchId, productKey, field, value);

    if (field === 'usTrackingId') {
      const tracking = String(value || '').trim();
      if (tracking.length >= 4) {
        const batch = batchesRef.current.find((entry) => entry.id === batchId);
        const firstLine = (batch?.lines || []).find(
          (line) => getProcurementProductKey(line) === productKey
        );
        if (firstLine?.id) {
          void fetchHawbForTrackingLine(batchId, firstLine.id, tracking, {
            quiet: true,
            propagate: true,
          });
        }
      }
    }
  };

  /** Persist group buy qty across lines so tracking upload can tally vs Excel shipped qty. */
  const handleProductGroupBuyQtyChange = (batchId, productKey, rawValue) => {
    const totalBuy = Math.max(0, Math.floor(Number(rawValue) || 0));
    let updatedBatchLines = [];
    setBatches((current) =>
      current.map((batch) => {
        if (batch.id !== batchId) return batch;
        const groupLines = (batch.lines || []).filter(
          (line) => getProcurementProductKey(line) === productKey
        );
        if (!groupLines.length) return batch;
        const distributed = distributeBuyQuantityAcrossLines(groupLines, totalBuy);
        const byId = new Map(distributed.map((line) => [line.id, line]));
        const newLines = batch.lines.map((line) => byId.get(line.id) || line);
        updatedBatchLines = newLines;
        return { ...batch, lines: newLines };
      })
    );
    batchesRef.current = batchesRef.current.map((batch) =>
      batch.id === batchId ? { ...batch, lines: updatedBatchLines } : batch
    );
  };

  /** Copy buy-side fields from one line onto every sibling with the same SKU/ASIN. */
  const applyBuyFieldsToProductSiblings = (batchId, sourceLineId) => {
    const sourceBatch = batchesRef.current.find((batch) => batch.id === batchId);
    const sourceLine = sourceBatch?.lines?.find((line) => line.id === sourceLineId);
    if (!sourceLine) return;

    const productKey = getProcurementProductKey(sourceLine);
    const buyFields = {
      purchasePrice: sourceLine.purchasePrice,
      buyMarketplace: sourceLine.buyMarketplace,
      buyCarrierName: sourceLine.buyCarrierName,
      usOrderId: sourceLine.usOrderId,
      usTrackingId: sourceLine.usTrackingId,
      hawb: sourceLine.hawb,
      productLink: sourceLine.productLink,
      inventoryStatus: getUIStatus(sourceLine.inventoryStatus),
    };

    let appliedCount = 0;
    const updatedLines = (sourceBatch.lines || []).map((line) => {
      if (line.id === sourceLineId) return line;
      if (getProcurementProductKey(line) !== productKey) return line;
      const status = getUIStatus(line.inventoryStatus);
      if (status === 'Customer Service' || status === 'Not feasible') return line;
      appliedCount += 1;
      let next = {
        ...line,
        confirmed: false,
        ...Object.fromEntries(
          Object.entries(buyFields).filter(
            ([, value]) => value !== undefined && value !== null && value !== ''
          )
        ),
      };
      if (buyFields.inventoryStatus) {
        next = applyStatusSideEffects(
          next,
          'inventoryStatus',
          buyFields.inventoryStatus,
          inventoryList
        );
      }
      return next;
    });

    setBatches((current) =>
      current.map((batch) => (batch.id === batchId ? { ...batch, lines: updatedLines } : batch))
    );
    batchesRef.current = batchesRef.current.map((batch) =>
      batch.id === batchId ? { ...batch, lines: updatedLines } : batch
    );

    if (appliedCount === 0) {
      showStatusToast({
        type: 'warning',
        message: 'No sibling lines for this product to update.',
      });
      return;
    }

    showStatusToast({
      type: 'success',
      message: `Buy fields copied to ${appliedCount} same-product line(s). Adjust exceptions, then Save.`,
    });

    if (batchId !== inlineEditingBatchId) {
      scheduleInlineSave(batchId);
    }
  };

  const handleBulkConfirmComplete = (summary) => {
    if (!summary?.succeeded?.length) return;

    const successByLine = new Map(
      summary.succeeded.map((item) => [item.lineId, item.payload])
    );
    const successOrderIds = new Set(summary.succeeded.map((item) => item.orderId));

    setBatches((prevBatches) =>
      prevBatches.map((b) => ({
        ...b,
        lines: b.lines.map((l) => {
          const order = l.orderItem?.order;
          if (!order?.id) return l;

          const payload = successByLine.get(l.id);
          const matchedOrder = successOrderIds.has(order.id);
          if (!payload && !matchedOrder) return l;

          const updatedOrder = payload?.order;
          return {
            ...l,
            orderItem: {
              ...l.orderItem,
              order: {
                ...order,
                ...(updatedOrder || {}),
                trackingPushed: true,
                status: 'shipped',
                trackingDummy:
                  payload?.trackingNumber ||
                  updatedOrder?.trackingDummy ||
                  order.trackingDummy,
              },
            },
          };
        }),
      }))
    );

    setSelectedBulkLineIds(new Set());
  };

  const handleConfirmShipmentSuccess = (lineId, payload) => {
    if (!lineId) {
      setAlertInfo({
        isOpen: true,
        title: 'Success',
        message: payload?.message || 'Shipment confirmed on Amazon Seller Central.',
        tone: 'success',
      });
      setConfirmShipmentTarget(null);
      return;
    }

    const updatedOrder = payload?.order;
    setBatches((prevBatches) =>
      prevBatches.map((b) => ({
        ...b,
        lines: b.lines.map((l) => {
          if (l.id !== lineId || !l.orderItem?.order) {
            return l;
          }
          return {
            ...l,
            orderItem: {
              ...l.orderItem,
              order: {
                ...l.orderItem.order,
                ...(updatedOrder || {}),
                trackingPushed: true,
                status: 'shipped',
                trackingDummy:
                  payload?.trackingNumber ||
                  updatedOrder?.trackingDummy ||
                  l.orderItem.order.trackingDummy,
              },
            },
          };
        }),
      }))
    );
    setAlertInfo({
      isOpen: true,
      title: 'Success',
      message: payload?.message || 'Shipment confirmed on Amazon Seller Central.',
      tone: 'success',
    });
    setConfirmShipmentTarget(null);
  };

  const applyBatchFromServer = (batch) => {
    if (!batch?.id) return;
    const vanished =
      batch.status === 'received' || batch.status === 'cancelled';

    setBatches((current) => {
      let next;
      if (vanished) {
        next = current.filter((entry) => entry.id !== batch.id);
      } else {
        next = current.some((entry) => entry.id === batch.id)
          ? current.map((entry) => (entry.id === batch.id ? batch : entry))
          : [batch, ...current];
      }
      batchesRef.current = next;
      return next;
    });

    setBatchOptions((current) => {
      if (vanished) {
        return current.filter((entry) => entry.id !== batch.id);
      }
      return current.map((entry) =>
        entry.id === batch.id
          ? {
              ...entry,
              name: batch.name || entry.name,
              status: batch.status || entry.status,
              lineCount: (batch.lines || []).length,
              fill: getBatchFillSummary(batch, inventoryList),
            }
          : entry
      );
    });
  };

  const handleConfirmLine = async (batchId, lineId, forceNotFeasible = false) => {
    const batch = batches.find((b) => b.id === batchId);
    if (!batch) return;

    const lineIndex = batch.lines.findIndex((l) => l.id === lineId);
    if (lineIndex === -1) return;

    const line = batch.lines[lineIndex];
    const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
    const sku = line.orderItem?.sku || line.sku;
    const invItem = inventoryList.find(
      (item) =>
        (sku && item.sku === sku) ||
        (asin && (item.asin === asin || item.asin === line.orderItem?.asinCom || item.asin === line.orderItem?.asinIn))
    );
    const stockVal = invItem ? invItem.quantityOnHand : (line.orderItem?.quantityOnHand || 0);
    const statusVal = getUIStatus(line.inventoryStatus);

    if (statusVal === 'Amazon') {
      const hasUsOrder = !!line.usOrderId?.trim();
      const hasBuyingPrice = line.purchasePrice !== undefined && line.purchasePrice !== null && line.purchasePrice !== '' && Number(line.purchasePrice) > 0;
      if (!hasUsOrder || !hasBuyingPrice) {
        showStatusToast({
          type: 'error',
          message:
            "US Order ID and Buying Price are mandatory to confirm under 'Amazon' status.",
        });
        return;
      }
    } else if (statusVal === 'Inventory') {
      if (stockVal < line.quantity) {
        showStatusToast({
          type: 'error',
          message: `Confirm failed: Stock (${stockVal}) is less than ordered quantity (${line.quantity}) for status 'Inventory'.`,
        });
        return;
      }
    } else if (statusVal === 'Customer Service') {
      if (!line.supplierNotes?.trim()) {
        showStatusToast({
          type: 'error',
          message: `Confirm failed: Remark is mandatory for status 'Customer Service'.`,
        });
        return;
      }
    } else if (statusVal === 'Not feasible') {
      if (!line.supplierNotes?.trim()) {
        showStatusToast({
          type: 'error',
          message: `Confirm failed: Remark is mandatory for status 'Not feasible'.`,
        });
        return;
      }
      if (!forceNotFeasible) {
        setConfirmNotFeasibleLine({ batchId, lineId });
        return;
      }
    }

    try {
      setStatus({ type: 'loading', message: 'Confirming line...' });

      const clearBuyFields =
        statusVal === 'Inventory' ||
        statusVal === 'Customer Service' ||
        statusVal === 'Not feasible';
      let purchasePrice = null;
      if (statusVal === 'Inventory') {
        purchasePrice = invItem ? invItem.unitCost : 0;
      } else if (statusVal === 'Customer Service' || statusVal === 'Not feasible') {
        purchasePrice = 0;
      }

      if (statusVal === 'Customer Service') {
        addPriorityOrderId(line.orderItem?.order?.id);
      }

      const payload = await setProcurementLineConfirmed(token, batchId, lineId, {
        confirmed: true,
        clearBuyFields,
        purchasePrice,
      });

      if (payload?.batch) {
        applyBatchFromServer(payload.batch);
      }

      const movedToFulfillment =
        payload?.batch?.status === 'received' ||
        (Boolean(line.hawb?.trim()) && payload?.batch?.status !== 'cancelled');

      setStatus({
        type: 'success',
        message:
          payload?.batch?.status === 'received'
            ? 'All lines fulfilled — batch moved out of Procurement into Fulfillment.'
            : statusVal === 'Customer Service'
              ? 'Line confirmed and pushed to Customer Service priority.'
              : movedToFulfillment && Boolean(line.hawb?.trim())
                ? 'Line confirmed with HAWB — now in Fulfillment.'
                : 'Line confirmed.',
      });
    } catch (error) {
      console.error('Failed to confirm line:', error);
      setStatus({ type: 'error', message: `Confirm failed: ${error.message}` });
    }
  };

  const handleUnconfirmLine = async (batchId, lineId) => {
    try {
      setStatus({ type: 'loading', message: 'Unconfirming line...' });
      const payload = await setProcurementLineConfirmed(token, batchId, lineId, {
        confirmed: false,
      });
      if (payload?.batch) {
        applyBatchFromServer(payload.batch);
      }
      setStatus({ type: 'success', message: 'Line unconfirmed.' });
    } catch (error) {
      console.error('Failed to unconfirm line:', error);
      setStatus({ type: 'error', message: `Unconfirm failed: ${error.message}` });
    }
  };

  const handleInlineSave = async (batchId) => {
    try {
      const snapshot = batchesRef.current.find((b) => b.id === batchId);
      const linesSnapshot = (snapshot?.lines || []).map((line) => ({ ...line }));
      if (!linesSnapshot.length) return;
      batchLoadSeq.current += 1;

      await enqueueBatchRequest(batchId, async () => {
        const latestBatch = batchesRef.current.find((b) => b.id === batchId);
        const latestHasEdits = (latestBatch?.lines || []).some((line) => {
          const price = Number(line.purchasePrice);
          return (
            (Number.isFinite(price) && price > 0) ||
            String(line.usOrderId || '').trim() ||
            String(line.usTrackingId || '').trim() ||
            String(line.hawb || '').trim()
          );
        });
        const lines = latestHasEdits ? latestBatch.lines : linesSnapshot;

        const payload = await updateProcurementBatch(token, batchId, {
          lines: lines.map((line) => toProcurementLinePayload(line)),
        });
        if (payload?.batch) {
          batchLoadSeq.current += 1;
          applyBatchFromServer(payload.batch);
          if (payload.batch.status === 'received') {
            setStatus({
              type: 'success',
              message: 'All lines fulfilled — batch moved out of Procurement into Fulfillment.',
            });
          }
        }
      });
    } catch (error) {
      console.error('Failed to save inline changes:', error);
      setStatus({ type: 'error', message: `Inline save failed: ${error.message}` });
      throw error;
    }
  };

  const setQuietSaveState = (batchId, state) => {
    setQuietSaveByBatch((current) => {
      if (current[batchId] === state) return current;
      return { ...current, [batchId]: state };
    });
  };

  /** Debounced autosave — tabbing fields won't thrash the network or the UI. */
  const scheduleInlineSave = useCallback(
    (batchId) => {
      if (!batchId) return;
      if (batchId === inlineEditingBatchId) return;

      if (inlineSaveTimers.current[batchId]) {
        clearTimeout(inlineSaveTimers.current[batchId]);
      }
      if (quietSaveClearTimers.current[batchId]) {
        clearTimeout(quietSaveClearTimers.current[batchId]);
      }

      setQuietSaveState(batchId, 'pending');
      inlineSaveTimers.current[batchId] = setTimeout(async () => {
        setQuietSaveState(batchId, 'saving');
        try {
          await handleInlineSave(batchId);
          setQuietSaveState(batchId, 'saved');
          quietSaveClearTimers.current[batchId] = setTimeout(() => {
            setQuietSaveState(batchId, '');
          }, 1400);
        } catch {
          setQuietSaveState(batchId, 'error');
        }
      }, 420);
    },
    // handleInlineSave closes over token/enqueue; re-bind when edit mode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inlineEditingBatchId, token]
  );

  useEffect(() => {
    return () => {
      Object.values(inlineSaveTimers.current).forEach(clearTimeout);
      Object.values(quietSaveClearTimers.current).forEach(clearTimeout);
    };
  }, []);

  const startInlineBatchEdit = (batch) => {
    if (batch.status === 'received' || batch.status === 'cancelled') {
      showStatusToast({
        type: 'error',
        message: 'Received or cancelled batches cannot be edited.',
      });
      return;
    }
    setInlineEditBackup(JSON.parse(JSON.stringify(batch)));
    setInlineEditingBatchId(batch.id);
    setInlineEditStartedAt(new Date());
    setBulkHawbInput('');
    setBulkUsOrderId('');
    setBulkUsTrackingId('');
    setSelectedBulkLineIds(new Set());
    closeBatchEdit();
  };

  const cancelInlineBatchEdit = () => {
    if (inlineEditBackup) {
      setBatches((current) =>
        current.map((batch) => (batch.id === inlineEditBackup.id ? inlineEditBackup : batch))
      );
      batchesRef.current = batchesRef.current.map((batch) =>
        batch.id === inlineEditBackup.id ? inlineEditBackup : batch
      );
    }
    setInlineEditingBatchId(null);
    setInlineEditBackup(null);
    setInlineEditStartedAt(null);
    setBulkHawbInput('');
    setBulkUsOrderId('');
    setBulkUsTrackingId('');
    setSelectedBulkLineIds(new Set());
  };

  const selectBatchFromDropdown = (batchId) => {
    if (!batchId || batchId === selectedBatchId) return;
    if (inlineEditingBatchId && inlineEditingBatchId !== batchId) {
      cancelInlineBatchEdit();
    }
    setSelectedBulkLineIds(new Set());
    selectedBatchIdRef.current = batchId;
    setSelectedBatchId(batchId);
    loadBatchDetail(batchId)
      .then((detail) => {
        if (!detail) return;
        const detailOption = normalizeBatchOption(detail, inventoryList);
        setBatchOptions((current) =>
          current.map((batch) =>
            batch.id === detail.id ? { ...batch, ...detailOption } : batch
          )
        );
      })
      .catch((error) => {
        setStatus({ type: 'error', message: error.message });
      });
  };

  const applyHawbToAllLines = (batchId) => {
    const value = bulkHawbInput.trim();
    if (!value) {
      showStatusToast({ type: 'error', message: 'Enter a HAWB number to apply to all lines.' });
      return;
    }

    let updatedBatchLines = [];
    setBatches((current) =>
      current.map((batch) => {
        if (batch.id !== batchId) return batch;
        updatedBatchLines = batch.lines.map((line) => ({ ...line, hawb: value }));
        return { ...batch, lines: updatedBatchLines };
      })
    );
    batchesRef.current = batchesRef.current.map((batch) => {
      if (batch.id !== batchId) return batch;
      return { ...batch, lines: updatedBatchLines };
    });
    showStatusToast({ type: 'success', message: 'HAWB applied to all lines. Edit individual rows if some differ, then Save.' });
  };

  const toggleBulkLineSelected = (lineId) => {
    setSelectedBulkLineIds((current) => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const selectEligibleBulkLines = (batch) => {
    const ids = batch.lines
      .filter((line) => {
        const status = getUIStatus(line.inventoryStatus);
        return status !== 'Customer Service' && status !== 'Not feasible';
      })
      .map((line) => line.id);
    setSelectedBulkLineIds(new Set(ids));
  };

  const applyUsFieldsToSelectedLines = async (batchId) => {
    const usOrder = bulkUsOrderId.trim();
    const usTracking = bulkUsTrackingId.trim();
    let hawbValue = bulkHawbInput.trim();

    if (!usOrder && !usTracking && !hawbValue) {
      showStatusToast({
        type: 'error',
        message: 'Enter a US Order ID, Tracking ID, and/or HAWB, then apply to selected lines.',
      });
      return;
    }

    if (selectedBulkLineIds.size === 0) {
      showStatusToast({
        type: 'error',
        message: 'Select the lines that share these buy details (checkboxes), then apply.',
      });
      return;
    }

    const selectedIds = selectedBulkLineIds;
    const sourceBatch = batchesRef.current.find((batch) => batch.id === batchId);
    if (!sourceBatch) return;

    // One lookup per tracking — never N calls for the same tracking in a batch.
    if (usTracking && !hawbValue) {
      const firstSelected = sourceBatch.lines.find((line) => selectedIds.has(line.id));
      hawbValue = await fetchHawbForTrackingLine(
        batchId,
        firstSelected?.id || sourceBatch.lines[0]?.id,
        usTracking,
        { quiet: true, propagate: false }
      );
      if (hawbValue) {
        setBulkHawbInput(hawbValue);
      }
    } else if (hawbValue && usTracking) {
      rememberHawbForTracking(usTracking, hawbValue);
    }

    const latestBatch = batchesRef.current.find((batch) => batch.id === batchId) || sourceBatch;

    let appliedCount = 0;
    const updatedBatchLines = latestBatch.lines.map((line) => {
      if (!selectedIds.has(line.id)) return line;
      const status = getUIStatus(line.inventoryStatus);
      if (status === 'Customer Service' || status === 'Not feasible') return line;

      appliedCount += 1;
      return {
        ...line,
        confirmed: false,
        ...(usOrder ? { usOrderId: usOrder } : {}),
        ...(usTracking ? { usTrackingId: usTracking } : {}),
        ...(hawbValue ? { hawb: hawbValue } : {}),
      };
    });

    setBatches((current) =>
      current.map((batch) => (batch.id === batchId ? { ...batch, lines: updatedBatchLines } : batch))
    );
    batchesRef.current = batchesRef.current.map((batch) =>
      batch.id === batchId ? { ...batch, lines: updatedBatchLines } : batch
    );

    if (hawbValue && usTracking) {
      applyHawbToMatchingTracking(batchId, usTracking, hawbValue);
    }

    if (appliedCount === 0) {
      showStatusToast({
        type: 'warning',
        message: 'No eligible lines updated (Customer Service / Not feasible lines are skipped).',
      });
      return;
    }

    const parts = [];
    if (usOrder) parts.push('US Order ID');
    if (usTracking) parts.push('US Tracking');
    if (hawbValue) parts.push('HAWB');
    showStatusToast({
      type: 'success',
      message: `${parts.join(' + ')} applied to ${appliedCount} line(s). Adjust any exceptions, then Save.`,
    });
  };

  const saveInlineBatchEdit = async () => {
    if (!inlineEditingBatchId) return;
    setSavingInlineEdit(true);
    try {
      await handleInlineSave(inlineEditingBatchId);
      setInlineEditingBatchId(null);
      setInlineEditBackup(null);
      setInlineEditStartedAt(null);
      setBulkHawbInput('');
      setBulkUsOrderId('');
      setBulkUsTrackingId('');
      setSelectedBulkLineIds(new Set());
      showStatusToast({ type: 'success', message: 'Batch updated.' });
    } catch (error) {
      showStatusToast({ type: 'error', message: error.message || 'Failed to save batch.' });
    } finally {
      setSavingInlineEdit(false);
    }
  };

  /** Buy details (price, US order/tracking, HAWB, etc.) only while Edit is active. */
  const canEditBatchLines = (batch) => batch.id === inlineEditingBatchId;

  const isFieldLocked = (batch, line, stockVal) =>
    batch.id !== inlineEditingBatchId ||
    (line.confirmed && isLineConfirmed(line, stockVal));

  const saveBatchEdit = async () => {
    if (!editingName.trim()) {
      setStatus({ type: 'error', message: 'Batch name is required.' });
      return;
    }

    const lines = editingLines.filter((line) => line.orderItemId || line.selectedProductId);

    if (!lines.length) {
      setStatus({ type: 'error', message: 'Add at least one line with an order item or product.' });
      return;
    }

    // Validate quantities, selection, and status rules
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const title = line.orderItem?.title || line.title || `Line ${i + 1}`;
      if (!line.quantity || Number(line.quantity) < 1) {
        setStatus({ type: 'error', message: `Line ${i + 1} (${title}): Quantity is mandatory and must be at least 1.` });
        return;
      }
      if (!line.orderItemId && !line.selectedProductId) {
        setStatus({ type: 'error', message: `Line ${i + 1} (${title}): Product selection is mandatory for custom lines.` });
        return;
      }
      if (!line.orderItemId && (!line.companyName?.trim() || !line.productLink?.trim())) {
        setStatus({
          type: 'error',
          message: `Line ${i + 1} (${title}): Company name and product link are mandatory.`,
        });
        return;
      }

      const statusVal = getUIStatus(line.inventoryStatus);

      // Validation 2: Inventory can only be selected when stock is available
      const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
      const sku = line.orderItem?.sku || line.sku;
      const invItem = inventoryList.find(
        (item) =>
          (sku && item.sku === sku) ||
          (asin && (item.asin === asin || item.asin === line.orderItem?.asinCom || item.asin === line.orderItem?.asinIn))
      );
      const stockVal = invItem ? invItem.quantityOnHand : (line.orderItem?.quantityOnHand || 0);

      if (statusVal === 'Inventory' && stockVal <= 0) {
        setStatus({
          type: 'error',
          message: `Line ${i + 1} (${title}): Status 'Inventory' can only be selected when stock is available.`,
        });
        return;
      }
    }

    setSavingEdit(true);

    try {
      await updateProcurementBatch(token, editingBatch.id, {
        status: editingStatus,
        name: editingName.trim(),
        lines: lines.map((line) => toProcurementLinePayload(line)),
      });

      setStatus({ type: 'success', message: 'Procurement batch updated.' });
      closeBatchEdit();
      await loadBatches();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleProductChange = (index, productId, isDraft = true) => {
    const prod = productsList.find((p) => p.id === productId);
    const updateFn = isDraft ? setDraftLines : setEditingLines;

    updateFn((current) =>
      current.map((line, lineIndex) => {
        if (lineIndex === index) {
          if (!prod) {
            return {
              ...line,
              selectedProductId: '',
              asin: '',
              sku: '',
              price: '',
              title: '',
              productSearchTerm: '',
              sellingPrice: '',
              label: 'Custom procurement line',
              orderItem: undefined,
            };
          }
          return {
            ...line,
            selectedProductId: productId,
            asin: prod.asin || '',
            sku: prod.sellerSku || '',
            price: prod.price || '',
            sellingPrice: prod.price || '',
            title: prod.title || '',
            label: prod.title || 'Selected Product',
            orderItem: {
              id: productId,
              asinIn: prod.asin,
              asinCom: prod.asin,
              sku: prod.sellerSku,
              title: prod.title,
              imageUrl: prod.imageUrl,
            }
          };
        }
        return line;
      })
    );
  };

  const renderLineFields = (line, index, isDraft) => {
    const updateFn = isDraft ? updateDraftLine : updateEditLine;
    const isCustom = !line.orderItemId;
    const title = line.orderItem?.title || line.label || 'Custom line';



    return (
      <div
        key={`${line.label}-${index}`}
        className="rounded-lg border border-slate-200 bg-white"
      >
        <div className="flex items-start gap-3 border-b border-slate-100 px-4 py-3">
          {line.orderItem ? (
            <ProductThumb
              item={line.orderItem}
              marketplace={line.orderItem.order?.marketplace}
              className="h-11 w-11 shrink-0"
            />
          ) : (
            <span
              className="inline-grid h-11 w-11 shrink-0 place-items-center rounded-md border border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
              aria-hidden="true"
            >
              SKU
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900" title={title}>
                  {title}
                </p>
                {line.orderItem?.order?.amazonOrderId ? (
                  <p className="mt-0.5 font-mono text-xs text-slate-500">
                    {line.orderItem.order.amazonOrderId}
                    {line.orderItem.asinIn || line.orderItem.asinCom
                      ? ` · ${line.orderItem.asinIn || line.orderItem.asinCom}`
                      : ''}
                    {line.orderItem.sku ? ` · ${line.orderItem.sku}` : ''}
                  </p>
                ) : (
                  <p className="mt-0.5 text-xs text-slate-500">
                    {isCustom
                      ? line.selectedProductId
                        ? 'Catalog product selected'
                        : 'Select a catalog product for this line'
                      : 'Order line'}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-red-600"
                onClick={() => {
                  if (isDraft) {
                    setDraftLines((current) => current.filter((_, i) => i !== index));
                  } else {
                    setEditingLines((current) => current.filter((_, i) => i !== index));
                  }
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-3 px-4 py-3 sm:grid-cols-12">
          {isCustom ? (
            <label className="grid gap-1.5 sm:col-span-12 relative">
              <span className={labelClassName}>Product</span>
              <div className="relative">
                <input
                  type="text"
                  className={`${inputClassName} pr-10`}
                  placeholder="Search by name, ASIN or SKU…"
                  value={line.productSearchTerm !== undefined && line.productSearchTerm !== '' ? line.productSearchTerm : (line.orderItem?.title || '')}
                  onFocus={() => {
                    setFocusedLineIndex(index);
                    setFocusedLineIsDraft(isDraft);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setFocusedLineIndex(null);
                    }, 200);
                  }}
                  onChange={(e) => {
                    const newTerm = e.target.value;
                    updateFn(index, 'productSearchTerm', newTerm);
                  }}
                  required={!line.selectedProductId}
                />
                {line.selectedProductId && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg font-bold"
                    onClick={() => {
                      handleProductChange(index, '', isDraft);
                      updateFn(index, 'productSearchTerm', '');
                    }}
                    title="Clear selection"
                  >
                    &times;
                  </button>
                )}
              </div>
              {((focusedLineIndex === index && focusedLineIsDraft === isDraft) || line.productSearchTerm) && (
                <ul className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto divide-y divide-slate-150">
                  {(() => {
                    const term = (line.productSearchTerm || '').trim();
                    if (term.length < 2) {
                      return (
                        <li className="px-4 py-3 text-sm text-slate-400 text-center list-none bg-white">
                          Type at least 2 characters to search Amazon listings
                        </li>
                      );
                    }
                    if (productSearchLoading) {
                      return (
                        <li className="px-4 py-3 text-sm text-slate-400 text-center list-none bg-white">
                          Searching…
                        </li>
                      );
                    }
                    if (pickerProducts.length === 0) {
                      return (
                        <li className="px-4 py-3 text-sm text-slate-400 text-center list-none bg-white">
                          No matching products found
                        </li>
                      );
                    }
                    return pickerProducts.slice(0, 25).map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center gap-3 px-3.5 py-2.5 hover:bg-slate-50/80 cursor-pointer transition-all list-none bg-white"
                        onMouseDown={() => {
                          handleProductChange(index, p.id, isDraft);
                          updateFn(index, 'productSearchTerm', '');
                          setFocusedLineIndex(null);
                        }}
                      >
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt=""
                            className="h-10 w-10 rounded object-cover border border-slate-200 bg-slate-50 shrink-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="inline-grid h-10 w-10 shrink-0 place-items-center rounded border border-slate-200 bg-slate-50 text-[9px] font-semibold text-slate-400">
                            NO IMG
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-800" title={p.title}>
                            {p.title}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
                              {p.marketplace || 'Market'}
                            </span>
                            {p.asin && (
                              <span className="font-mono bg-slate-50 px-1 py-0.5 rounded text-[10px] text-slate-600">
                                ASIN: {p.asin}
                              </span>
                            )}
                            {p.sellerSku && (
                              <span className="font-mono bg-slate-50 px-1 py-0.5 rounded text-[10px] text-slate-600 truncate max-w-[150px]">
                                SKU: {p.sellerSku}
                              </span>
                            )}
                            {p.price != null && p.price !== '' && (
                              <span className="font-semibold text-emerald-600 ml-auto">
                                ₹{Number(p.price).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    ));
                  })()}
                </ul>
              )}
            </label>
          ) : null}

          {isCustom && line.selectedProductId ? (
            <div className="grid grid-cols-4 gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:col-span-12">
              <div>
                <span className="block font-medium text-slate-500">ASIN</span>
                <span className="mt-0.5 block truncate font-mono text-slate-800">{line.asin || '—'}</span>
              </div>
              <div>
                <span className="block font-medium text-slate-500">SKU</span>
                <span className="mt-0.5 block truncate font-mono text-slate-800">{line.sku || '—'}</span>
              </div>
              <div>
                <span className="block font-medium text-slate-500">Stock (Inv)</span>
                <span className="mt-0.5 block font-semibold text-slate-800">{(() => {
                  const invItem = inventoryList.find(
                    (item) =>
                      (line.sku && item.sku === line.sku) ||
                      (line.asin && (item.asin === line.asin || item.asin === line.asinCom || item.asin === line.asinIn))
                  );
                  return invItem ? `${invItem.quantityOnHand} units` : '0 units';
                })()}</span>
              </div>
              <div>
                <span className="block font-medium text-slate-500">Selling Price</span>
                <span className="mt-0.5 block font-semibold text-slate-800">
                  {line.price != null && line.price !== '' ? `₹${Number(line.price).toFixed(2)}` : '—'}
                </span>
              </div>
            </div>
          ) : null}

          {isCustom ? (
            <>
              <label className="grid gap-1.5 sm:col-span-6">
                <span className={labelClassName}>
                  Company name <span className="text-red-500">*</span>
                </span>
                <input
                  className={inputClassName}
                  value={line.companyName || ''}
                  onChange={(event) => updateFn(index, 'companyName', event.target.value)}
                  placeholder="e.g. Acme Supplies"
                  required
                />
              </label>
              <label className="grid gap-1.5 sm:col-span-6">
                <span className={labelClassName}>
                  Product link <span className="text-red-500">*</span>
                </span>
                <input
                  className={inputClassName}
                  type="url"
                  value={line.productLink || ''}
                  onChange={(event) => updateFn(index, 'productLink', event.target.value)}
                  placeholder="https://supplier.com/product"
                  required
                />
              </label>
            </>
          ) : null}

          <label className={`grid gap-1.5 ${isCustom ? 'sm:col-span-12' : 'sm:col-span-3'}`}>
            <span className={labelClassName}>{isCustom ? 'Qty' : 'Qty ordered'}</span>
            <input
              className={inputClassName}
              type="number"
              min="1"
              value={line.quantity || ''}
              disabled={!isCustom}
              onChange={(event) =>
                updateFn(index, 'quantity', event.target.value === '' ? '' : Number(event.target.value))
              }
              required={isCustom}
            />
          </label>

          {!isCustom ? (
            <label className="grid gap-1.5 sm:col-span-3">
              <span className={labelClassName}>Extra qty</span>
              <input
                className={inputClassName}
                type="number"
                min="0"
                value={line.extraQuantity || 0}
                onChange={(event) => updateFn(index, 'extraQuantity', Number(event.target.value))}
              />
            </label>
          ) : null}

          <label className="grid gap-1.5 sm:col-span-12">
            <span className={labelClassName}>Remarks</span>
            <div className="flex items-start gap-2">
              <input
                className={inputClassName}
                value={line.supplierNotes || ''}
                onChange={(event) => updateFn(index, 'supplierNotes', event.target.value)}
                placeholder="Supplier notes…"
              />
              <RemarkHistoryButton
                lineId={line.id}
                orderId={line.orderId || line.orderItem?.order?.id}
                onClick={() =>
                  setRemarkHistoryTarget({
                    lineId: line.id || null,
                    orderId: line.orderId || line.orderItem?.order?.id || null,
                  })
                }
              />
            </div>
          </label>
        </div>
      </div>
    );
  };

  const handleRenameBatch = (batch) => {
    setRenameBatchTarget(batch);
  };

  const handleRenameBatchConfirm = async (newName) => {
    if (!renameBatchTarget?.id) return;
    const trimmed = String(newName || '').trim();
    if (!trimmed) {
      setAlertInfo({
        isOpen: true,
        title: 'Required Field',
        message: 'Batch name cannot be empty.',
        tone: 'warning',
      });
      return;
    }

    try {
      setRenamingBatch(true);
      setStatus({ type: 'loading', message: 'Renaming batch...' });
      await updateProcurementBatch(token, renameBatchTarget.id, { name: trimmed });
      setRenameBatchTarget(null);
      setStatus({ type: 'success', message: 'Batch renamed.' });
      await loadBatches();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setRenamingBatch(false);
    }
  };

  const handleCancelBatchConfirm = async () => {
    if (!cancelBatchId) return;
    const batchId = cancelBatchId;
    setCancelBatchId(null);
    setDeletingBatchId(batchId);

    try {
      await deleteProcurementBatch(token, batchId);
      setStatus({ type: 'success', message: 'Procurement batch cancelled.' });
      await loadBatches();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setDeletingBatchId(null);
    }
  };

  // Only draft → ordered. No Mark shipped / Mark received.
  const nextStatusMap = useMemo(
    () => ({
      draft: 'ordered',
    }),
    []
  );

  const hasCustomLine = draftLines.some((line) => !line.orderItemId);

  return (
    <section className="grid gap-5 min-w-0 w-full">
      <PageHeader
        title="Procurement"
        subtitle="Create purchase batches from order lines and track receipt into inventory."
        actions={
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={importUnshipped}
              disabled={importing}
            >
              {importing ? 'Importing...' : 'Import unshipped orders'}
            </button>
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={startCustomBatch}
            >
              Custom procurement
            </button>
            {batches.length ? (
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={handleClearAllClick}
                disabled={clearing || importing}
              >
                {clearing ? 'Clearing...' : 'Clear all batches'}
              </button>
            ) : null}
          </div>
        }
      />

      <StatusBanner status={status} setStatus={setStatus} />

      {draftLines.length ? (
        <article className={`${panelClassName} w-full overflow-hidden`}>
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">New batch draft</h3>
              <p className="mt-0.5 text-sm text-slate-500">
                {draftLines.length} line{draftLines.length === 1 ? '' : 's'} ready to save
              </p>
            </div>
            <button
              type="button"
              className={`${secondaryButtonClassName} h-9 shrink-0 self-center`}
              onClick={() => {
                setDraftLines([]);
                setDraftBatchName('');
              }}
            >
              Discard draft
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            <label className="grid max-w-xl gap-1.5">
              <span className={labelClassName}>Batch label (optional)</span>
              <input
                className={inputClassName}
                value={draftBatchName}
                onChange={(e) => setDraftBatchName(e.target.value)}
                placeholder="Auto ID like BH0101 — optional note…"
              />
              <span className="text-xs text-slate-500">
                A unique batch ID (BH0001, BH0002, …) is assigned automatically when you create the batch.
              </span>
            </label>

            {hasCustomLine ? (
              <div className="space-y-3">
                {draftLines.map((line, index) => renderLineFields(line, index, true))}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full min-w-[50rem] border-collapse text-left text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3">Product</th>
                      <th className="px-5 py-3 w-32">Qty Ordered</th>
                      <th className="px-5 py-3 w-36">Extra Qty</th>
                      <th className="px-5 py-3">Remarks</th>
                      <th className="px-5 py-3 w-20 text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {draftLines.map((line, index) => {
                      const title = line.orderItem?.title || line.label || 'Order line';
                      return (
                        <tr key={index} className="hover:bg-slate-50/50">
                          <td className="px-5 py-4">
                            <div className="flex items-start gap-3">
                              {line.orderItem ? (
                                <ProductThumb
                                  item={line.orderItem}
                                  marketplace={line.orderItem.order?.marketplace}
                                  className="h-10 w-10 shrink-0"
                                />
                              ) : (
                                <span className="inline-grid h-10 w-10 shrink-0 place-items-center rounded-md border border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase text-slate-400">
                                  SKU
                                </span>
                              )}
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="font-semibold text-slate-900 line-clamp-2" title={title}>
                                    {title}
                                  </p>
                                  {line.isReorder ? <ReorderBadge /> : null}
                                </div>
                                {line.orderItem?.order?.amazonOrderId && (
                                  <p className="mt-1 font-mono text-xs text-slate-500">
                                    {line.orderItem.order.amazonOrderId}
                                    {line.orderItem.asinIn || line.orderItem.asinCom
                                      ? ` · ${line.orderItem.asinIn || line.orderItem.asinCom}`
                                      : ''}
                                    {line.orderItem.sku ? ` · ${line.orderItem.sku}` : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <input
                              className={`${inputClassName} w-20 text-center bg-slate-50 text-slate-500`}
                              type="number"
                              value={line.quantity || ''}
                              disabled
                            />
                          </td>
                          <td className="px-5 py-4">
                            <input
                              className={`${inputClassName} w-28 text-center`}
                              type="number"
                              min="0"
                              value={line.extraQuantity || 0}
                              onChange={(event) => updateDraftLine(index, 'extraQuantity', Number(event.target.value))}
                            />
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-start gap-2">
                              <input
                                className={inputClassName}
                                value={line.supplierNotes || ''}
                                onChange={(event) => updateDraftLine(index, 'supplierNotes', event.target.value)}
                                placeholder="Supplier notes…"
                              />
                              <RemarkHistoryButton
                                lineId={line.id}
                                orderId={line.orderId || line.orderItem?.order?.id}
                                onClick={() =>
                                  setRemarkHistoryTarget({
                                    lineId: line.id || null,
                                    orderId: line.orderId || line.orderItem?.order?.id || null,
                                  })
                                }
                              />
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <button
                              type="button"
                              className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-red-600"
                              onClick={() => setDraftLines((current) => current.filter((_, i) => i !== index))}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-5 py-3">
            {hasCustomLine && (
              <button
                type="button"
                className={`${secondaryButtonClassName} h-9 shrink-0`}
                onClick={addDraftBlankLine}
              >
                + Add line
              </button>
            )}
            <button
              type="button"
              className={`${primaryButtonClassName} h-9 shrink-0${!hasCustomLine ? ' ml-auto' : ''}`}
              onClick={createBatch}
              disabled={creating}
            >
              {creating ? 'Creating…' : 'Create batch'}
            </button>
          </div>
        </article>
      ) : null}

      {editingBatch ? (
        <article className={`${panelClassName} w-full overflow-hidden`}>
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div>
              <h3 className="text-[15px] font-semibold tracking-tight text-slate-900">Edit batch</h3>
              <p className="mt-0.5 text-sm text-slate-500">
                Update status, quantities, and supplier notes
              </p>
            </div>
            <button
              type="button"
              className={`${secondaryButtonClassName} h-9 shrink-0 self-center`}
              onClick={closeBatchEdit}
            >
              Close
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5">
                <span className={labelClassName}>
                  Batch name <span className="text-red-500">*</span>
                </span>
                <input
                  className={inputClassName}
                  value={editingName}
                  onChange={(event) => setEditingName(event.target.value)}
                  placeholder="Batch name"
                  required
                />
              </label>
              <label className="grid gap-1.5">
                <span className={labelClassName}>Status</span>
                <select
                  className={inputClassName}
                  value={editingStatus}
                  onChange={(event) => setEditingStatus(event.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="ordered">Ordered</option>
                  <option value="shipped">Shipped</option>
                  <option value="received">Received</option>
                </select>
              </label>
            </div>

            <div className="space-y-3">
              {editingLines.map((line, index) => renderLineFields(line, index, false))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-5 py-3">
            <button
              type="button"
              className={`${secondaryButtonClassName} h-9 shrink-0`}
              onClick={addEditLine}
            >
              + Add line
            </button>
            <button
              type="button"
              className={`${primaryButtonClassName} h-9 shrink-0`}
              onClick={saveBatchEdit}
              disabled={savingEdit}
            >
              {savingEdit ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </article>
      ) : null}

      {status.type === 'loading' && !batches.length ? (
        <PageLoading label="Loading procurement batches…" />
      ) : batches.length ? (
        <div className="space-y-4 min-w-0 w-full">
          <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2.5">
                  <h3 className="text-sm font-semibold text-slate-900">Batches</h3>
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                    {batchPagination.total.toLocaleString('en-IN')} total
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  {batchDays !== 'all' ? `Showing batches from last ${batchDays} days` : 'Showing all batches'} · Select a batch to review lines
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
                    Batch:
                  </span>
                  <select
                    className="h-9 w-64 sm:w-80 rounded-lg border border-slate-200 bg-slate-50/50 px-3 text-xs font-medium text-slate-800 shadow-sm transition hover:bg-white focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                    value={selectedBatchId || ''}
                    onChange={(event) => selectBatchFromDropdown(event.target.value)}
                    title="Incomplete = missing order/tracking/price · Ok not confirmed = details filled · Confirmed = line confirmed"
                  >
                    {batchOptions.map((batch) => {
                      const name =
                        batch.batchCode || batch.name || `Batch ${String(batch.id).slice(0, 8)}`;
                      const resolved = resolveBatchOptionFill(batch, inventoryList);
                      const fillLabel = formatBatchFillLabel(resolved.fill);
                      const label =
                        batch.batchCode && batch.name && batch.name !== batch.batchCode
                          ? `${batch.batchCode} · ${batch.name}`
                          : name;
                      return (
                        <option key={batch.id} value={batch.id}>
                          {batch.status} · {label} · {fillLabel}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
                    Date:
                  </span>
                  <select
                    className="h-9 rounded-lg border border-slate-200 bg-slate-50/50 px-3 text-xs font-medium text-slate-800 shadow-sm transition hover:bg-white focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                    value={batchDays}
                    onChange={(event) => {
                      setBatchDays(event.target.value);
                    }}
                  >
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="180">Last 180 days</option>
                    <option value="all">All time</option>
                  </select>
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
                    Status:
                  </span>
                  <select
                    className="h-9 rounded-lg border border-slate-200 bg-slate-50/50 px-3 text-xs font-medium text-slate-800 shadow-sm transition hover:bg-white focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                    value={batchStatus}
                    onChange={(event) => {
                      setBatchStatus(event.target.value);
                    }}
                  >
                    <option value="">Active batches</option>
                    <option value="draft">Draft</option>
                    <option value="ordered">Ordered</option>
                    <option value="shipped">Shipped</option>
                    <option value="received">Received</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
              </div>
            </div>
          </div>
          {[selectedBatch].filter(Boolean).map((batch) => {
            const customBatch = isCustomBatch(batch);
            const isEditingBatch = batch.id === inlineEditingBatchId;
            // HAWB is part of buy logistics — always visible; optional.
            const showHawbCols = batch.status !== 'cancelled';
            // Dummy tracking is generated on Create batch — show it in draft/ordered too (not only shipped).
            const showDummyTrackingCols = !customBatch;
            const linesEditable = canEditBatchLines(batch);
            // Price / US order / tracking / HAWB / carrier — only after clicking Edit.
            // Excel Upload Tracking ID still updates via its own page (unchanged).
            const buyIdsEditable = isEditingBatch;
            const showSelectCol = linesEditable || showDummyTrackingCols || isEditingBatch;
            const confirmReadySelectedCount = (batch.lines || []).filter(
              (line) => selectedBulkLineIds.has(line.id) && isLineConfirmReady(line)
            ).length;
            const fillSummary = getBatchFillSummary(batch, inventoryList);
            const batchBuyingByMarket = (batch.lines || []).reduce((acc, line) => {
              const total = getLinePurchaseTotal(line);
              if (total == null || total <= 0) return acc;
              const code = line.buyMarketplace || 'US';
              acc[code] = (acc[code] || 0) + total;
              return acc;
            }, {});
            const batchBuyingTotalEntries = Object.entries(batchBuyingByMarket);
            const batchTotalBuyQty = (batch.lines || []).reduce(
              (sum, line) => sum + getProcurementLineQty(line),
              0
            );
            const colsBeforeCurrency = customBatch ? 8 : 9;
            const colsAfterBuyingPrice =
              1 + // buy total
              3 +
              (showDummyTrackingCols ? 2 : 0) +
              (showHawbCols ? 1 : 0) +
              5;
            const tableColCount =
              (showSelectCol ? 1 : 0) +
              colsBeforeCurrency +
              1 + // buying price currency/market spacer in footer uses buying col
              1 + // buying price
              colsAfterBuyingPrice;
            const visibleBatchLines = (batch.lines || []).filter(
              (line) =>
                !(getUIStatus(line.inventoryStatus) === 'Customer Service' && line.confirmed)
            );
            const tableEntries = buildProcurementTableEntries(
              visibleBatchLines,
              customBatch ? 'lines' : batchLinesView,
              expandedProductKeys
            );
            return (
              <article
                key={batch.id}
                className={`${panelClassName} w-full transition-[box-shadow,border-color] duration-200 ${
                  isEditingBatch
                    ? 'border-sky-300 shadow-[0_0_0_1px_rgba(14,165,233,0.25),0_12px_28px_-18px_rgba(15,23,42,0.35)]'
                    : 'hover:shadow-[0_10px_24px_-18px_rgba(15,23,42,0.28)]'
                }`}
              >
                {/* Clean Batch Header Bar */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200/90 bg-white px-6 py-4">
                  <div className="min-w-0">
                    <h3 className="flex flex-wrap items-center gap-2.5 text-base font-semibold tracking-tight text-slate-900">
                      <span className="truncate font-mono tracking-wide text-slate-900">
                        {batch.batchCode || batch.name || `Batch ${batch.id.slice(0, 8)}`}
                      </span>
                      {batch.batchCode && batch.name && batch.name !== batch.batchCode ? (
                        <span className="truncate text-sm font-medium text-slate-500">{batch.name}</span>
                      ) : null}
                      {isEditingBatch ? (
                        <span className="rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800 ring-1 ring-inset ring-sky-200">
                          Editing
                        </span>
                      ) : null}
                      {fillSummary.incomplete > 0 ? (
                        <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200">
                          Incomplete {fillSummary.incomplete}
                        </span>
                      ) : null}
                      {fillSummary.ready > 0 ? (
                        <span className="rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-800 ring-1 ring-inset ring-sky-200">
                          Ok not confirmed {fillSummary.ready}
                        </span>
                      ) : null}
                      {fillSummary.confirmed > 0 ? (
                        <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
                          Confirmed {fillSummary.confirmed}
                        </span>
                      ) : null}
                      <QuietSavePill state={quietSaveByBatch[batch.id]} />
                      <button
                        type="button"
                        className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        onClick={() => handleRenameBatch(batch)}
                        title="Rename batch"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    </h3>
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>{batch.lines.length} line{batch.lines.length === 1 ? '' : 's'}</span>
                      <span className="text-slate-300">•</span>
                      <span>Created {new Date(batch.createdAt).toLocaleString('en-IN')}</span>
                      {batch.updatedAt && new Date(batch.updatedAt).getTime() !== new Date(batch.createdAt).getTime() ? (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="font-medium text-slate-700">
                            Last edit {new Date(batch.updatedAt).toLocaleString('en-IN')}
                          </span>
                        </>
                      ) : null}
                      <span className="text-slate-300">•</span>
                      <span className="font-medium text-slate-600">{batch.creatorName || 'System'}</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <BatchStatusBadge status={batch.status} />
                    {customBatch ? <CustomBatchBadge /> : null}

                    {batch.status === 'draft' && nextStatusMap.draft ? (
                      (() => {
                        const allLinesConfirmed =
                          fillSummary.total > 0 &&
                          fillSummary.confirmed === fillSummary.total;
                        const blocked = !allLinesConfirmed;
                        return (
                          <button
                            type="button"
                            className={`${secondaryButtonClassName} h-9 shrink-0 disabled:cursor-not-allowed disabled:opacity-50`}
                            disabled={blocked}
                            title={
                              blocked
                                ? `Confirm every line in Confirm Status first (${fillSummary.confirmed}/${fillSummary.total} confirmed)`
                                : 'Mark this batch as Ordered'
                            }
                            onClick={() => {
                              if (blocked) {
                                setStatus({
                                  type: 'error',
                                  message:
                                    'Confirm every line in Confirm Status before marking the batch as ordered.',
                                });
                                return;
                              }
                              advanceStatus(batch.id, 'ordered');
                            }}
                          >
                            Mark ordered
                          </button>
                        );
                      })()
                    ) : null}

                    {batch.id === inlineEditingBatchId ? (
                      <>
                        <button
                          type="button"
                          className={`${primaryButtonClassName} h-9 shrink-0`}
                          onClick={saveInlineBatchEdit}
                          disabled={savingInlineEdit}
                        >
                          {savingInlineEdit ? 'Saving…' : 'Save changes'}
                        </button>
                        <button
                          type="button"
                          className={`${secondaryButtonClassName} h-9 shrink-0`}
                          onClick={cancelInlineBatchEdit}
                          disabled={savingInlineEdit}
                          title="Discard edits and exit edit mode"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {batch.status !== 'received' && batch.status !== 'cancelled' ? (
                          <button
                            type="button"
                            className={`${secondaryButtonClassName} h-9 shrink-0`}
                            onClick={() => startInlineBatchEdit(batch)}
                          >
                            Edit
                          </button>
                        ) : null}
                        {batch.status === 'shipped' || batch.status === 'received' ? (
                          <span
                            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-400"
                            title={
                              batch.status === 'shipped'
                                ? 'Cancel is blocked after the batch is shipped'
                                : 'Cancel is blocked after the batch is received'
                            }
                          >
                            <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path
                                fillRule="evenodd"
                                d="M10 1a9 9 0 100 18 9 9 0 000-18zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Cancel blocked
                          </span>
                        ) : batch.status !== 'cancelled' ? (
                          <button
                            type="button"
                            className={`${secondaryButtonClassName} h-9 shrink-0`}
                            onClick={() => setCancelBatchId(batch.id)}
                            disabled={deletingBatchId === batch.id}
                          >
                            {deletingBatchId === batch.id ? 'Cancelling…' : 'Cancel'}
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>

                {/* Dedicated Shared Buy Logistics Drawer */}
                {isEditingBatch ? (
                  <div className="border-b border-sky-100 bg-sky-50/50 px-6 py-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-900/70">
                          Shared buy details
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          Select lines → fill fields → Apply. Same tracking reuses one HAWB.
                          {!customBatch && batchLinesView === 'product'
                            ? ' In By product view: Select group, then Apply — or use Apply buy → group on a price cell.'
                            : null}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className={`${secondaryButtonClassName} h-9 shrink-0 text-xs`}
                          onClick={() => selectEligibleBulkLines(batch)}
                        >
                          Select all eligible
                        </button>
                        <button
                          type="button"
                          className={`${secondaryButtonClassName} h-9 shrink-0 text-xs`}
                          onClick={() => setSelectedBulkLineIds(new Set())}
                        >
                          Clear selection
                        </button>
                        <button
                          type="button"
                          className={`${primaryButtonClassName} h-9 shrink-0 text-xs`}
                          onClick={() => applyUsFieldsToSelectedLines(batch.id)}
                        >
                          Apply to selected ({selectedBulkLineIds.size})
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 max-w-3xl">
                      <label className="grid gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          US Order ID
                        </span>
                        <input
                          type="text"
                          value={bulkUsOrderId}
                          onChange={(e) => setBulkUsOrderId(e.target.value)}
                          placeholder="Same order for multiple SKUs…"
                          className="h-9 w-full rounded border border-slate-200 bg-white px-2 py-1.5 font-mono text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
                        />
                      </label>
                      <label className="grid gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          US Tracking ID
                        </span>
                        <input
                          type="text"
                          value={bulkUsTrackingId}
                          onChange={(e) => setBulkUsTrackingId(e.target.value)}
                          placeholder="Same tracking for multiple SKUs…"
                          className="h-9 w-full rounded border border-slate-200 bg-white px-2 py-1.5 font-mono text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
                        />
                      </label>
                      <label className="grid gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          HAWB (optional)
                        </span>
                        <input
                          type="text"
                          value={bulkHawbInput}
                          onChange={(e) => setBulkHawbInput(e.target.value)}
                          placeholder="Auto from tracking or enter…"
                          className="h-9 w-full rounded border border-slate-200 bg-white px-2 py-1.5 font-mono text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
                        />
                      </label>
                    </div>
                  </div>
                ) : batch.status !== 'cancelled' && batch.status !== 'received' ? (
                  <div className="border-b border-slate-100 bg-slate-50/40 px-6 py-2 text-xs text-slate-500">
                    Click <span className="font-semibold text-slate-700">Edit</span> to change price, US Order ID, Tracking, HAWB, and other buy details.
                    Tracking can also be filled via <span className="font-semibold text-slate-700">Upload Tracking ID</span>.
                  </div>
                ) : null}

                {/* Table Controls Toolbar */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/90 bg-white px-6 py-2.5">
                  <div className="flex items-center gap-3">
                    {!customBatch ? (
                      <div className="inline-flex h-9 overflow-hidden rounded-md border border-slate-200 bg-white text-xs font-semibold">
                        <button
                          type="button"
                          className={`px-3 transition-colors ${
                            batchLinesView === 'lines'
                              ? 'bg-slate-900 text-white'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                          onClick={() => setBatchLinesView('lines')}
                        >
                          By order
                        </button>
                        <button
                          type="button"
                          className={`px-3 transition-colors ${
                            batchLinesView === 'product'
                              ? 'bg-slate-900 text-white'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                          onClick={() => setBatchLinesView('product')}
                          title="Group same SKU/ASIN and show total qty to buy"
                        >
                          By product
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    {showDummyTrackingCols ? (
                      <>
                        <button
                          type="button"
                          className={`${secondaryButtonClassName} h-9 shrink-0`}
                          onClick={() => selectConfirmReadyLines(batch)}
                        >
                          Select ready to confirm
                        </button>
                        <button
                          type="button"
                          className={`${primaryButtonClassName} h-9 shrink-0`}
                          onClick={() => openBulkConfirmShipment(batch)}
                          disabled={confirmReadySelectedCount === 0}
                        >
                          Confirm selected ({confirmReadySelectedCount})
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
                <div
                  className="w-full max-h-[min(70vh,52rem)] overflow-auto overscroll-y-auto [scrollbar-gutter:stable]"
                  ref={(node) => {
                    if (!node || node.dataset.wheelBound === '1') return;
                    node.dataset.wheelBound = '1';
                    node.addEventListener('wheel', chainWheelToPageScroll, { passive: false });
                  }}
                >
                  <table className={`w-full border-collapse ${customBatch ? (showHawbCols ? 'min-w-[128rem]' : 'min-w-[120rem]') : showDummyTrackingCols || showHawbCols ? 'min-w-[166rem]' : 'min-w-[134rem]'}`}>
                    <thead>
                      <tr>
                        {showSelectCol ? (
                          <th className={`${PROCUREMENT_TH_CLASS} w-10 px-2`}>
                            <span className="sr-only">Select</span>
                          </th>
                        ) : null}
                        <th className={`${PROCUREMENT_TH_CLASS} w-28`}>Order Date</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-28`}>Ship By</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-16`}>Image</th>
                        <th className={`${PROCUREMENT_TH_CLASS} min-w-[280px]`}>Product Name</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-24`}>
                          {customBatch
                            ? 'Qty'
                            : batchLinesView === 'product'
                              ? 'Buy Qty'
                              : 'Ordered Qty'}
                        </th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-20`}>Stock</th>
                        {!customBatch ? (
                          <th className={`${PROCUREMENT_TH_CLASS} w-24`}>Extra Qty</th>
                        ) : null}
                        <th className={`${PROCUREMENT_TH_CLASS} w-28`}>Selling Price</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-28`}>Total Value</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-44`}>Buy Market</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-36`}>Buying Price</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-32`}>Buy Total</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-40`}>US Order ID</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-40`}>US Tracking ID</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-44`}>Carrier Name</th>
                        {showDummyTrackingCols && (
                          <>
                            <th className={`${PROCUREMENT_TH_CLASS} w-44`}>Dummy Tracking ID</th>
                            <th className={`${PROCUREMENT_TH_CLASS} w-44`}>Confirm shipment</th>
                          </>
                        )}
                        {showHawbCols ? (
                          <th className={`${PROCUREMENT_TH_CLASS} w-44`}>HAWB</th>
                        ) : null}
                        <th className={`${PROCUREMENT_TH_CLASS} w-36`}>
                          {customBatch ? 'Company Name' : 'Type'}
                        </th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-28`}>Status</th>
                        <th className={`${PROCUREMENT_TH_CLASS} min-w-[180px]`}>Remark</th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-28`}>
                          {customBatch ? 'Product Link' : 'Buy Link'}
                        </th>
                        <th className={`${PROCUREMENT_TH_CLASS} w-32`}>Confirm status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableEntries.map((entry) => {
                        if (entry.type === 'group') {
                          const group = entry.group;
                          const readyCount = buildConfirmTargetsFromLines(group.lines).length;
                          const shippedCount = group.lines.filter(
                            (line) => line.orderItem?.order?.trackingPushed
                          ).length;
                          const orderLineCount = group.lines.filter(
                            (line) => line.orderItem?.order?.id
                          ).length;
                          const allOrdersShipped =
                            orderLineCount > 0 && shippedCount === orderLineCount;
                          const collapsed = !expandedProductKeys.has(group.key);
                          const allSelected = group.lines.every((line) =>
                            selectedBulkLineIds.has(line.id)
                          );
                          const groupLead = group.lines[0];
                          const groupBuyMarket = getBuyMarketplace(groupLead?.buyMarketplace);
                          const shared = (field, read = (line) => line?.[field]) => {
                            const values = group.lines.map((line) => {
                              const raw = read(line);
                              return raw === undefined || raw === null ? '' : String(raw);
                            });
                            const first = values[0] || '';
                            const mixed = values.some((value) => value !== first);
                            return { value: first, mixed };
                          };
                          const sharedMarket = shared('buyMarketplace');
                          const sharedPrice = shared('purchasePrice');
                          const sharedUsOrder = shared('usOrderId');
                          const sharedUsTracking = shared('usTrackingId');
                          const sharedCarrier = shared('buyCarrierName');
                          const sharedHawb = shared('hawb');
                          const sharedType = shared('inventoryStatus', (line) =>
                            getUIStatus(line.inventoryStatus)
                          );
                          const groupStock = group.lines.reduce((sum, line) => {
                            const lineSku = line.orderItem?.sku || line.sku;
                            const lineAsin =
                              line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
                            const invItem = inventoryList.find(
                              (item) =>
                                (lineSku && item.sku === lineSku) ||
                                (lineAsin &&
                                  (item.asin === lineAsin ||
                                    item.asin === line.orderItem?.asinCom ||
                                    item.asin === line.orderItem?.asinIn))
                            );
                            return (
                              sum +
                              (invItem
                                ? invItem.quantityOnHand
                                : line.orderItem?.quantityOnHand || 0)
                            );
                          }, 0);
                          const colsAfterType =
                            4 + // Status, Remark, Buy Link, Confirm status
                            (showDummyTrackingCols ? 0 : 0);
                          // Group header: product identity only (date/ship/image/name), then qty columns stay visible.
                          const productIdentityColSpan = 4;
                          const orderedSum = group.lines.reduce(
                            (sum, line) =>
                              sum +
                              Math.max(
                                1,
                                Number(line.orderItem?.quantityOrdered || line.quantity || 1) || 1
                              ),
                            0
                          );
                          const extraSum = group.lines.reduce(
                            (sum, line) => sum + Math.max(0, Number(line.extraQuantity || 0) || 0),
                            0
                          );

                          const blurSaveGroup = () => {
                            if (batch.id === inlineEditingBatchId) return;
                            scheduleInlineSave(batch.id);
                          };

                          return (
                            <tr
                              key={`group-${group.key}`}
                              className="border-y border-sky-200 bg-sky-50/90"
                            >
                              {showSelectCol ? (
                                <td className={`${tableCellClassName} w-10 text-center`}>
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    checked={allSelected}
                                    onChange={() => {
                                      if (allSelected) {
                                        setSelectedBulkLineIds((current) => {
                                          const next = new Set(current);
                                          group.lines.forEach((line) => next.delete(line.id));
                                          return next;
                                        });
                                      } else {
                                        selectProductGroupLines(group.lines);
                                      }
                                    }}
                                    aria-label="Select all lines for this product"
                                  />
                                </td>
                              ) : null}
                              <td
                                colSpan={productIdentityColSpan}
                                className={`${tableCellClassName}`}
                              >
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold text-sky-900 hover:bg-sky-100"
                                    onClick={() => toggleProductGroupExpanded(group.key)}
                                  >
                                    <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
                                    {collapsed ? 'Show orders' : 'Hide orders'}
                                  </button>
                                  <ProductThumb
                                    item={groupLead?.orderItem || groupLead}
                                    marketplace={groupLead?.orderItem?.order?.marketplace}
                                    token={token}
                                    className="h-9 w-9"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold text-slate-900">
                                      {group.title}
                                    </p>
                                    <p className="text-[11px] text-slate-500">
                                      {group.sku ? `SKU ${group.sku}` : null}
                                      {group.sku && group.asin ? ' · ' : null}
                                      {group.asin ? `ASIN ${group.asin}` : null}
                                      {' · '}
                                      {group.lines.length} customer order
                                      {group.lines.length === 1 ? '' : 's'}
                                      <span className="ml-1 font-medium text-sky-800">
                                        · fill buy fields once → applies to all
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </td>
                              {/* Total buy quantity — primary qty for this product */}
                              <td className={`${tableCellClassName} w-24 whitespace-nowrap`}>
                                {linesEditable ? (
                                  <div className="grid gap-0.5">
                                    <input
                                      type="number"
                                      min={0}
                                      step={1}
                                      className="w-16 rounded border border-sky-200 bg-white px-2 py-1 text-center text-sm font-bold text-slate-900 focus:border-blue-500 focus:outline-none"
                                      value={group.totalQty}
                                      onChange={(e) =>
                                        handleProductGroupBuyQtyChange(
                                          batch.id,
                                          group.key,
                                          e.target.value
                                        )
                                      }
                                      onBlur={blurSaveGroup}
                                      title="Total Amazon buy qty for this product"
                                    />
                                    <span className="text-[10px] font-medium text-slate-500">
                                      ordered {orderedSum}
                                      {extraSum > 0 ? ` + extra ${extraSum}` : ''}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="grid gap-0.5">
                                    <span className="text-sm font-bold text-slate-900">{group.totalQty}</span>
                                    <span className="text-[10px] font-medium text-slate-500">
                                      ordered {orderedSum}
                                      {extraSum > 0 ? ` + extra ${extraSum}` : ''}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className={`${tableCellClassName} w-20 whitespace-nowrap text-sm text-slate-700`}>
                                {groupStock}
                              </td>
                              {!customBatch ? (
                                <td className={`${tableCellClassName} w-24 whitespace-nowrap text-sm text-slate-700`}>
                                  {extraSum}
                                </td>
                              ) : null}
                              <td className={`${tableCellClassName} w-28 whitespace-nowrap text-slate-400`}>—</td>
                              <td className={`${tableCellClassName} w-28 whitespace-nowrap text-slate-400`}>—</td>
                              {/* Buy Market */}
                              <td className={`${tableCellClassName} w-44 whitespace-nowrap`}>
                                {linesEditable ? (
                                  <select
                                    className="w-full max-w-[10.5rem] rounded border border-sky-200 bg-white px-2 py-1 text-sm font-medium text-slate-700 focus:border-blue-500 focus:outline-none"
                                    value={sharedMarket.value || 'US'}
                                    onChange={(e) =>
                                      handleProductGroupBuyFieldChange(
                                        batch.id,
                                        group.key,
                                        'buyMarketplace',
                                        e.target.value
                                      )
                                    }
                                    onBlur={blurSaveGroup}
                                    title={sharedMarket.mixed ? 'Mixed — changing sets all' : undefined}
                                  >
                                    {BUY_MARKETPLACES.map((market) => (
                                      <option key={market.code} value={market.code}>
                                        {market.flag} {market.currency} · {market.domain}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="text-sm font-medium text-slate-700">
                                    {groupBuyMarket.flag} {groupBuyMarket.currency}
                                  </span>
                                )}
                              </td>
                              {/* Buying Price */}
                              <td className={`${tableCellClassName} w-36 whitespace-nowrap`}>
                                {linesEditable ? (
                                  <div className="relative w-28">
                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                                      <span className="text-xs font-semibold text-slate-400">
                                        {groupBuyMarket.symbol}
                                      </span>
                                    </div>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder={sharedPrice.mixed ? 'Mixed' : 'Price'}
                                      className="w-full rounded border border-sky-200 bg-white py-1 pl-7 pr-2 text-sm font-medium text-slate-700 focus:border-blue-500 focus:outline-none"
                                      value={sharedPrice.mixed ? '' : sharedPrice.value}
                                      onChange={(e) =>
                                        handleProductGroupBuyFieldChange(
                                          batch.id,
                                          group.key,
                                          'purchasePrice',
                                          e.target.value
                                        )
                                      }
                                      onBlur={blurSaveGroup}
                                    />
                                  </div>
                                ) : sharedPrice.value ? (
                                  formatBuyMoney(Number(sharedPrice.value), groupLead?.buyMarketplace)
                                ) : (
                                  '—'
                                )}
                              </td>
                              {/* Buy Total = buy qty × unit price */}
                              <td className={`${tableCellClassName} w-32 whitespace-nowrap`}>
                                {(() => {
                                  const groupTotal = group.lines.reduce((sum, line) => {
                                    const total = getLinePurchaseTotal(line);
                                    return sum + (total != null ? total : 0);
                                  }, 0);
                                  return groupTotal > 0
                                    ? (
                                        <span className="text-sm font-semibold text-slate-900">
                                          {formatBuyMoney(groupTotal, groupLead?.buyMarketplace)}
                                        </span>
                                      )
                                    : (
                                        <span className="text-sm text-slate-400">—</span>
                                      );
                                })()}
                              </td>
                              {/* US Order ID */}
                              <td className={`${tableCellClassName} w-40 whitespace-nowrap`}>
                                {buyIdsEditable ? (
                                  <input
                                    type="text"
                                    placeholder={sharedUsOrder.mixed ? 'Mixed' : 'US Order ID'}
                                    className="w-32 rounded border border-sky-200 bg-white px-2 py-1 font-mono text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
                                    value={sharedUsOrder.mixed ? '' : sharedUsOrder.value}
                                    onChange={(e) =>
                                      handleProductGroupBuyFieldChange(
                                        batch.id,
                                        group.key,
                                        'usOrderId',
                                        e.target.value
                                      )
                                    }
                                    onBlur={blurSaveGroup}
                                  />
                                ) : sharedUsOrder.value ? (
                                  <CopyableValue value={sharedUsOrder.value} />
                                ) : (
                                  '—'
                                )}
                              </td>
                              {/* US Tracking ID */}
                              <td className={`${tableCellClassName} w-40 whitespace-nowrap`}>
                                {buyIdsEditable ? (
                                  <input
                                    type="text"
                                    placeholder={sharedUsTracking.mixed ? 'Mixed' : 'US Tracking'}
                                    className="w-32 rounded border border-sky-200 bg-white px-2 py-1 font-mono text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
                                    value={sharedUsTracking.mixed ? '' : sharedUsTracking.value}
                                    onChange={(e) =>
                                      handleProductGroupBuyFieldChange(
                                        batch.id,
                                        group.key,
                                        'usTrackingId',
                                        e.target.value
                                      )
                                    }
                                    onBlur={blurSaveGroup}
                                  />
                                ) : sharedUsTracking.value ? (
                                  <CopyableValue value={sharedUsTracking.value} />
                                ) : (
                                  '—'
                                )}
                              </td>
                              {/* Carrier */}
                              <td className={`${tableCellClassName} w-44 whitespace-nowrap`}>
                                {linesEditable ? (
                                  <input
                                    type="text"
                                    placeholder={sharedCarrier.mixed ? 'Mixed' : 'Carrier'}
                                    className="w-36 rounded border border-sky-200 bg-white px-2 py-1 text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
                                    value={sharedCarrier.mixed ? '' : sharedCarrier.value}
                                    onChange={(e) =>
                                      handleProductGroupBuyFieldChange(
                                        batch.id,
                                        group.key,
                                        'buyCarrierName',
                                        e.target.value
                                      )
                                    }
                                    onBlur={blurSaveGroup}
                                  />
                                ) : (
                                  sharedCarrier.value || '—'
                                )}
                              </td>
                              {showDummyTrackingCols ? (
                                <>
                                  <td className={`${tableCellClassName} w-44 text-center text-xs text-slate-400`}>
                                    per order ↓
                                  </td>
                                  <td className={`${tableCellClassName} w-44 whitespace-nowrap text-center`}>
                                    {readyCount > 0 ? (
                                      <button
                                        type="button"
                                        className="inline-flex h-8 items-center gap-1 rounded bg-blue-600 px-2.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={() => openBulkConfirmForLines(group.lines)}
                                        title="Confirms each ready Amazon order in this product group"
                                      >
                                        {`Confirm ${readyCount} order${readyCount === 1 ? '' : 's'}`}
                                      </button>
                                    ) : allOrdersShipped ? (
                                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                                        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                        Shipped
                                      </span>
                                    ) : shippedCount > 0 ? (
                                      <span className="inline-flex flex-col items-center gap-0.5 text-xs font-semibold text-slate-500">
                                        <span className="inline-flex items-center gap-1 text-emerald-700">
                                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                          {shippedCount} shipped
                                        </span>
                                        <span className="text-slate-400">None ready</span>
                                      </span>
                                    ) : (
                                      <span className="inline-flex h-8 items-center rounded bg-slate-100 px-2.5 text-xs font-medium text-slate-500">
                                        None ready
                                      </span>
                                    )}
                                  </td>
                                </>
                              ) : null}
                              {showHawbCols ? (
                                <td className={`${tableCellClassName} w-44 whitespace-nowrap`}>
                                  {buyIdsEditable ? (
                                    <input
                                      type="text"
                                      placeholder={sharedHawb.mixed ? 'Mixed' : 'HAWB'}
                                      className="w-36 rounded border border-sky-200 bg-white px-2 py-1 font-mono text-sm text-slate-700 focus:border-blue-500 focus:outline-none"
                                      value={sharedHawb.mixed ? '' : sharedHawb.value}
                                      onChange={(e) =>
                                        handleProductGroupBuyFieldChange(
                                          batch.id,
                                          group.key,
                                          'hawb',
                                          e.target.value
                                        )
                                      }
                                      onBlur={blurSaveGroup}
                                    />
                                  ) : (
                                    sharedHawb.value || '—'
                                  )}
                                </td>
                              ) : null}
                              {/* Type */}
                              <td className={`${tableCellClassName} w-36 whitespace-nowrap`}>
                                {linesEditable ? (
                                  <select
                                    className="w-32 rounded border border-sky-200 bg-white px-2 py-1 text-sm font-medium text-slate-700 focus:border-blue-500 focus:outline-none"
                                    value={sharedType.mixed ? 'Amazon' : sharedType.value || 'Amazon'}
                                    onChange={(e) =>
                                      handleProductGroupBuyFieldChange(
                                        batch.id,
                                        group.key,
                                        'inventoryStatus',
                                        e.target.value
                                      )
                                    }
                                    onBlur={blurSaveGroup}
                                  >
                                    <option value="Amazon">Amazon</option>
                                    <option value="Inventory" disabled={groupStock <= 0}>
                                      Inventory {groupStock <= 0 ? '(No Stock)' : ''}
                                    </option>
                                    <option value="Customer Service">Customer Service</option>
                                    <option value="Not feasible">Not feasible</option>
                                  </select>
                                ) : (
                                  sharedType.value || '—'
                                )}
                              </td>
                              <td
                                colSpan={colsAfterType}
                                className={`${tableCellClassName} text-[11px] text-slate-500`}
                              >
                                Expand orders below to override any field one-by-one.
                              </td>
                            </tr>
                          );
                        }

                        const line = entry.line;
                        const rowIndented = Boolean(entry.indented);
                        const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
                        const sku = line.orderItem?.sku || line.sku;
                        const buyMarket = getBuyMarketplace(line.buyMarketplace);
                        const buyUrl = customBatch
                          ? line.productLink
                          : asin
                            ? amazonAsinUrl(asin, line.buyMarketplace)
                            : line.productLink;
                        const invItem = inventoryList.find(
                          (item) =>
                            (sku && item.sku === sku) ||
                            (asin && (item.asin === asin || item.asin === line.orderItem?.asinCom || item.asin === line.orderItem?.asinIn))
                        );
                        const stockVal = invItem ? invItem.quantityOnHand : (line.orderItem?.quantityOnHand || 0);

                        // Real-time Selling Price: Match SKU/ASIN in productsList
                        const activeListing = productsList.find(
                          (p) =>
                            (sku && p.sellerSku === sku) ||
                            (asin && (p.asin === asin || p.asin === line.orderItem?.asinCom || p.asin === line.orderItem?.asinIn))
                        );
                        const sellingPriceRealtime = activeListing ? activeListing.price : (line.sellingPrice || line.orderItem?.unitPrice || 0);
                        const orderedQty = line.orderItem?.quantityOrdered || line.quantity || 1;
                        const buyQty = getProcurementLineQty(line);
                        const buyingUnitPrice =
                          line.purchasePrice !== undefined && line.purchasePrice !== null && line.purchasePrice !== ''
                            ? Number(line.purchasePrice)
                            : null;
                        const buyingTotal = getLinePurchaseTotal(line);

                        // Total value fetched from unshipped order totalAmount, fallback to calculated total
                        const totalValue = (line.orderItem?.order?.totalAmount != null && Number(line.orderItem.order.totalAmount) !== 0)
                          ? Number(line.orderItem.order.totalAmount)
                          : (orderedQty * sellingPriceRealtime);
                        const totalValueCurrency = line.orderItem?.order?.currency || 'INR';

                        const orderStatus = line.orderItem?.order?.status;
                        const trackingPushed = Boolean(line.orderItem?.order?.trackingPushed);
                        const getDisplayOrderStatus = (status, pushed) => {
                          if (pushed) return 'shipped';
                          if (!status) return '—';
                          const lower = status.toLowerCase();
                          if (lower === 'pending' || lower === 'unshipped') return 'unshipped';
                          if (lower === 'shipped' || lower === 'delivered') return 'shipped';
                          return status;
                        };
                        const displayOrderStatus = getDisplayOrderStatus(orderStatus, trackingPushed);
                        const formatInDate = (value) =>
                          value
                            ? new Date(value).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : '—';
                        const hasOrderAndPrice =
                          !!String(line.usOrderId || '').trim() &&
                          line.purchasePrice !== undefined &&
                          line.purchasePrice !== null &&
                          line.purchasePrice !== '' &&
                          Number(line.purchasePrice) > 0;
                        const isRowSelected = selectedBulkLineIds.has(line.id);

                        return (
                          <tr
                            key={line.id}
                            className={`align-top transition-colors ${
                              rowIndented ? 'border-l-4 border-l-sky-200' : ''
                            } ${
                              isRowSelected
                                ? 'bg-blue-50/60 hover:bg-blue-50/80'
                                : hasOrderAndPrice
                                  ? 'bg-emerald-50 hover:bg-emerald-100/70'
                                  : rowIndented
                                    ? 'bg-slate-50/40 hover:bg-slate-50/80'
                                    : 'hover:bg-slate-50/50'
                            }`}
                          >
                            {showSelectCol ? (
                              <td className={`${tableCellClassName} w-10 text-center`}>
                                {getUIStatus(line.inventoryStatus) === 'Customer Service' ||
                                getUIStatus(line.inventoryStatus) === 'Not feasible' ? (
                                  <span className="text-slate-300">—</span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    checked={selectedBulkLineIds.has(line.id)}
                                    onChange={() => toggleBulkLineSelected(line.id)}
                                    aria-label="Select line"
                                  />
                                )}
                              </td>
                            ) : null}
                            {/* Order Date */}
                            <td className={`${tableCellClassName} w-28 whitespace-nowrap font-medium text-slate-700`}>
                              {formatInDate(line.orderItem?.order?.orderDate)}
                            </td>

                            {/* Ship By */}
                            <td className={`${tableCellClassName} w-28 whitespace-nowrap font-medium text-slate-700`}>
                              {formatInDate(line.orderItem?.order?.shipByDate)}
                            </td>

                            {/* Image */}
                            <td className={`${tableCellClassName} w-16 text-center`}>
                              {line.orderItem ? (
                                <ProductThumb
                                  item={line.orderItem}
                                  marketplace={line.orderItem.order?.marketplace}
                                  className="h-10 w-10 mx-auto"
                                />
                              ) : (
                                <span className="inline-grid h-10 w-10 shrink-0 place-items-center rounded-md border border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] text-[10px] font-semibold text-[rgb(var(--crm-text-muted))] mx-auto" aria-hidden="true">
                                  SKU
                                </span>
                              )}
                            </td>

                            {/* Product Name (ASIN, SKU and Customer Order ID underneath, copyable with one-click) */}
                            <td className={`${tableCellClassName} min-w-[280px]`}>
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="line-clamp-2 text-sm font-medium text-slate-900 block" title={line.orderItem?.title || 'Manual line'}>
                                    {line.orderItem?.title || 'Manual line'}
                                  </span>
                                  {line.isReorder ? <ReorderBadge /> : null}
                                </div>
                                {(line.orderItem?.order?.amazonOrderId || asin || sku) && (
                                  <span className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-slate-500 block leading-normal">
                                    {line.orderItem?.order?.amazonOrderId ? (
                                      <>
                                        <span>Order ID</span>
                                        <CopyableValue value={line.orderItem.order.amazonOrderId} className="hover:bg-slate-100" />
                                        {(asin || sku) && <span aria-hidden="true">·</span>}
                                      </>
                                    ) : null}
                                    {asin ? (
                                      <>
                                        <CopyableValue label="ASIN" value={asin} className="hover:bg-slate-100" />
                                        {sku && <span aria-hidden="true">·</span>}
                                      </>
                                    ) : ''}
                                    {sku ? (
                                      <>
                                        <CopyableValue label="SKU" value={sku} className="hover:bg-slate-100" />
                                      </>
                                    ) : ''}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Ordered Quantity */}
                            <td className={`${tableCellClassName} w-24 text-center whitespace-nowrap`}>
                              <span className="font-medium text-slate-800">{orderedQty}</span>
                              {rowIndented ? (
                                <span className="mt-0.5 block text-[10px] font-medium text-slate-500">
                                  this order
                                </span>
                              ) : null}
                            </td>

                            {/* Stock */}
                            <td className={`${tableCellClassName} w-20 text-center whitespace-nowrap`}>
                              <span className="font-medium text-slate-700">{stockVal}</span>
                            </td>

                            {/* Extra Quantity — only for order-linked batches */}
                            {!customBatch ? (
                              <td className={`${tableCellClassName} w-24 text-center whitespace-nowrap`}>
                                {line.extraQuantity ?? 0}
                              </td>
                            ) : null}

                            {/* Selling Price */}
                            <td className={`${tableCellClassName} w-28 text-right whitespace-nowrap`}>
                              {sellingPriceRealtime != null && sellingPriceRealtime !== 0 ? `₹${Number(sellingPriceRealtime).toFixed(2)}` : '—'}
                            </td>

                            {/* Total Value */}
                            <td className={`${tableCellClassName} w-28 text-right whitespace-nowrap`}>
                              <span className="font-semibold text-emerald-700">
                                {totalValueCurrency === 'USD' ? '$' : totalValueCurrency === 'INR' || totalValueCurrency === '₹' ? '₹' : totalValueCurrency + ' '}
                                {Number(totalValue).toFixed(2)}
                              </span>
                            </td>

                            {/* Buy Market / Currency */}
                            <td className={`${tableCellClassName} w-44 whitespace-nowrap`}>
                              {linesEditable ? (
                                getUIStatus(line.inventoryStatus) === 'Customer Service' || getUIStatus(line.inventoryStatus) === 'Not feasible' ? (
                                  <span className="text-slate-400 font-medium pl-2">—</span>
                                ) : (
                                  <select
                                    disabled={isFieldLocked(batch, line, stockVal)}
                                    className={`border border-slate-200 rounded px-1.5 py-1 text-sm font-medium text-slate-700 w-40 focus:border-blue-500 focus:outline-none bg-white/70 hover:bg-slate-50 transition-colors ${(isFieldLocked(batch, line, stockVal)) ? 'opacity-65 bg-slate-50 cursor-not-allowed' : ''
                                      }`}
                                    value={line.buyMarketplace || 'US'}
                                    onChange={(e) => {
                                      handleInlineChange(batch.id, line.id, 'buyMarketplace', e.target.value);
                                      if (batch.id === inlineEditingBatchId) return;
                                      scheduleInlineSave(batch.id);
                                    }}
                                    title="Select source Amazon marketplace"
                                  >
                                    {BUY_MARKETPLACES.map((market) => (
                                      <option key={market.code} value={market.code}>
                                        {market.flag} {market.currency} · {market.domain}
                                      </option>
                                    ))}
                                  </select>
                                )
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700" title={buyMarket.label}>
                                  <span className="text-base leading-none" aria-hidden="true">{buyMarket.flag}</span>
                                  <span>{buyMarket.currency}</span>
                                  <span className="text-xs text-slate-500">{buyMarket.domain}</span>
                                </span>
                              )}
                            </td>

                            {/* Buying Price (editable inline if draft) */}
                            <td className={`${tableCellClassName} w-36 whitespace-nowrap`}>
                              {linesEditable ? (
                                getUIStatus(line.inventoryStatus) === 'Customer Service' || getUIStatus(line.inventoryStatus) === 'Not feasible' ? (
                                  <span className="text-slate-400 font-medium pl-2">—</span>
                                ) : (
                                  <div className="grid gap-1">
                                    <div className="relative rounded-md shadow-sm w-28">
                                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                                        <span className="text-slate-400 text-xs font-semibold">{buyMarket.symbol}</span>
                                      </div>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        disabled={isFieldLocked(batch, line, stockVal)}
                                        className={`border border-slate-200 rounded pl-7 pr-4 py-1 text-sm font-medium text-slate-700 w-full focus:border-blue-500 focus:outline-none bg-white/70 hover:bg-slate-50 focus:bg-white transition-colors ${(isFieldLocked(batch, line, stockVal)) ? 'opacity-65 bg-slate-50 cursor-not-allowed' : ''
                                          }`}
                                        value={line.purchasePrice || ''}
                                        onChange={(e) => handleInlineChange(batch.id, line.id, 'purchasePrice', e.target.value)}
                                        onBlur={() => {
                                      if (batch.id === inlineEditingBatchId) return;
                                      scheduleInlineSave(batch.id);
                                    }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') e.target.blur();
                                        }}
                                      />
                                      {getUIStatus(line.inventoryStatus) === 'Amazon' && (
                                        <span className="text-red-500 font-bold text-xs absolute right-1.5 top-1/2 -translate-y-1/2">*</span>
                                      )}
                                    </div>
                                    <span
                                      className={`min-h-[1rem] text-[11px] font-semibold pl-0.5 ${
                                        buyingTotal != null ? 'text-slate-600' : 'invisible'
                                      }`}
                                      aria-hidden={buyingTotal == null}
                                    >
                                      Total:{' '}
                                      {buyingTotal != null
                                        ? formatBuyMoney(buyingTotal, line.buyMarketplace)
                                        : '—'}
                                    </span>
                                    {!customBatch && batchLinesView === 'product' ? (
                                      <button
                                        type="button"
                                        className="text-left text-[10px] font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                                        onClick={() => applyBuyFieldsToProductSiblings(batch.id, line.id)}
                                        title="Push this line’s buy fields onto the other orders in this product group"
                                      >
                                        Apply override → group
                                      </button>
                                    ) : null}
                                  </div>
                                )
                              ) : buyingUnitPrice != null && buyingUnitPrice > 0 ? (
                                <div className="grid gap-0.5">
                                  <span className="font-medium text-slate-800">{formatBuyMoney(buyingUnitPrice, line.buyMarketplace)}</span>
                                  <span className="text-[11px] font-semibold text-slate-600">
                                    Total: {formatBuyMoney(buyingTotal ?? 0, line.buyMarketplace)}
                                  </span>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>

                            {/* Buy Total = buy qty × unit price (stored) */}
                            <td className={`${tableCellClassName} w-32 whitespace-nowrap`}>
                              {buyingTotal != null ? (
                                <div className="grid gap-0.5">
                                  <span className="text-sm font-semibold text-slate-900">
                                    {formatBuyMoney(buyingTotal, line.buyMarketplace)}
                                  </span>
                                  <span className="text-[10px] font-medium text-slate-500">
                                    {buyQty} × {buyingUnitPrice != null ? formatBuyMoney(buyingUnitPrice, line.buyMarketplace) : '—'}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>

                            {/* US Order ID — only while Editing */}
                            <td className={`${tableCellClassName} w-40 whitespace-nowrap`}>
                              {buyIdsEditable ? (
                                getUIStatus(line.inventoryStatus) === 'Customer Service' || getUIStatus(line.inventoryStatus) === 'Not feasible' ? (
                                  <span className="text-slate-400 font-medium pl-2">—</span>
                                ) : (
                                  <div className="relative inline-block w-32">
                                    <input
                                      type="text"
                                      placeholder="US Order ID"
                                      disabled={isFieldLocked(batch, line, stockVal)}
                                      className={`border border-slate-200 rounded pl-2 pr-4 py-1 text-sm font-mono text-slate-700 w-full focus:border-blue-500 focus:outline-none bg-white/70 hover:bg-slate-50 focus:bg-white transition-colors ${(isFieldLocked(batch, line, stockVal)) ? 'opacity-65 bg-slate-50 cursor-not-allowed' : ''
                                        }`}
                                      value={line.usOrderId || ''}
                                      onChange={(e) => handleInlineChange(batch.id, line.id, 'usOrderId', e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                      }}
                                    />
                                    {getUIStatus(line.inventoryStatus) === 'Amazon' && (
                                      <span className="text-red-500 font-bold text-xs absolute right-1.5 top-1/2 -translate-y-1/2">*</span>
                                    )}
                                  </div>
                                )
                              ) : (
                                line.usOrderId ? <CopyableValue value={line.usOrderId} /> : '—'
                              )}
                            </td>

                            {/* US Tracking ID — only while Editing */}
                            <td className={`${tableCellClassName} w-40 whitespace-nowrap`}>
                              {buyIdsEditable ? (
                                getUIStatus(line.inventoryStatus) === 'Customer Service' || getUIStatus(line.inventoryStatus) === 'Not feasible' ? (
                                  <span className="text-slate-400 font-medium pl-2">—</span>
                                ) : (
                                  <div className="grid gap-0.5">
                                    <input
                                      type="text"
                                      placeholder="US Tracking ID"
                                      disabled={isFieldLocked(batch, line, stockVal)}
                                      className={`border border-slate-200 rounded px-2 py-1 text-sm font-mono text-slate-700 w-32 focus:border-blue-500 focus:outline-none bg-white/70 hover:bg-slate-50 focus:bg-white transition-colors ${isFieldLocked(batch, line, stockVal) ? 'opacity-65 bg-slate-50 cursor-not-allowed' : ''
                                        }`}
                                      value={line.usTrackingId || ''}
                                      onChange={(e) => handleInlineChange(batch.id, line.id, 'usTrackingId', e.target.value)}
                                      onBlur={async () => {
                                        const latestLine = batchesRef.current
                                          .find((b) => b.id === batch.id)
                                          ?.lines?.find((l) => l.id === line.id);
                                        const latestTracking = String(
                                          latestLine?.usTrackingId || line.usTrackingId || ''
                                        ).trim();

                                        if (latestTracking) {
                                          await fetchHawbForTrackingLine(batch.id, line.id, latestTracking, {
                                            quiet: false,
                                            propagate: true,
                                          });
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.target.blur();
                                      }}
                                    />
                                    <span
                                      className={`min-h-[0.875rem] text-[10px] font-medium ${
                                        hawbLookupLineIds.has(line.id)
                                          ? 'text-blue-600'
                                          : 'invisible'
                                      }`}
                                      aria-hidden={!hawbLookupLineIds.has(line.id)}
                                    >
                                      Fetching HAWB…
                                    </span>
                                  </div>
                                )
                              ) : (
                                line.usTrackingId ? <CopyableValue value={line.usTrackingId} /> : '—'
                              )}
                            </td>

                            {/* Buy-market carrier name */}
                            <td className={`${tableCellClassName} w-44 whitespace-nowrap`}>
                              {linesEditable ? (
                                getUIStatus(line.inventoryStatus) === 'Customer Service' || getUIStatus(line.inventoryStatus) === 'Not feasible' ? (
                                  <span className="text-slate-400 font-medium pl-2">—</span>
                                ) : (
                                  <input
                                    type="text"
                                    placeholder={`${buyMarket.code} Carrier Name`}
                                    disabled={isFieldLocked(batch, line, stockVal)}
                                    className={`border border-slate-200 rounded px-2 py-1 text-sm font-medium text-slate-700 w-36 focus:border-blue-500 focus:outline-none bg-white/70 hover:bg-slate-50 focus:bg-white transition-colors ${(isFieldLocked(batch, line, stockVal)) ? 'opacity-65 bg-slate-50 cursor-not-allowed' : ''
                                      }`}
                                    value={line.buyCarrierName || ''}
                                    onChange={(e) => handleInlineChange(batch.id, line.id, 'buyCarrierName', e.target.value)}
                                    onBlur={() => {
                                      if (batch.id === inlineEditingBatchId) return;
                                      scheduleInlineSave(batch.id);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.target.blur();
                                    }}
                                    title={`${buyMarket.flag} ${buyMarket.label} carrier`}
                                  />
                                )
                              ) : line.buyCarrierName ? (
                                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
                                  <span className="text-xs text-slate-500">{buyMarket.code}</span>
                                  <CopyableValue value={line.buyCarrierName} />
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>

                            {/* Dummy Tracking ID & Push to Amazon */}
                            {showDummyTrackingCols && (
                              <>
                                <td className={`${tableCellClassName} w-44 whitespace-nowrap font-mono font-medium text-slate-700`}>
                                  {line.orderItem?.order?.trackingDummy || '—'}
                                </td>
                                <td className={`${tableCellClassName} w-44 whitespace-nowrap text-center`}>
                                  {line.orderItem?.order?.trackingPushed ? (
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                                      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                      </svg>
                                      Shipped
                                    </span>
                                  ) : line.orderItem?.order?.trackingDummy ? (
                                    <button
                                      type="button"
                                      onClick={() => openConfirmShipment(batch, line)}
                                      className="inline-flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 transition-colors shadow-sm"
                                      title={
                                        batchLinesView === 'product'
                                          ? 'Confirms this order and any other ready orders for the same SKU/ASIN'
                                          : 'Confirm shipment for this Amazon order'
                                      }
                                    >
                                      Confirm shipment
                                    </button>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>
                              </>
                            )}

                            {showHawbCols ? (
                              <td className={`${tableCellClassName} w-44 whitespace-nowrap`}>
                                {buyIdsEditable ? (
                                  <div className="grid gap-0.5">
                                    <input
                                      type="text"
                                      className="w-40 rounded border border-slate-200 px-2 py-1 text-sm font-medium text-slate-700 focus:border-blue-500 focus:outline-none"
                                      value={line.hawb || ''}
                                      onChange={(e) => {
                                        const next = e.target.value;
                                        handleInlineChange(batch.id, line.id, 'hawb', next);
                                        const tracking = String(
                                          batchesRef.current
                                            .find((b) => b.id === batch.id)
                                            ?.lines?.find((l) => l.id === line.id)?.usTrackingId ||
                                            line.usTrackingId ||
                                            ''
                                        ).trim();
                                        if (tracking && String(next || '').trim()) {
                                          rememberHawbForTracking(tracking, next);
                                        }
                                      }}
                                      onBlur={() => {
                                        const tracking = String(line.usTrackingId || '').trim();
                                        const hawb = String(
                                          batchesRef.current
                                            .find((b) => b.id === batch.id)
                                            ?.lines?.find((l) => l.id === line.id)?.hawb ||
                                            line.hawb ||
                                            ''
                                        ).trim();
                                        if (tracking && hawb) {
                                          applyHawbToMatchingTracking(batch.id, tracking, hawb);
                                        }
                                      }}
                                      placeholder="HAWB number"
                                      title="House Air Waybill"
                                    />
                                    {hawbLookupLineIds.has(line.id) ? (
                                      <span className="text-[10px] font-medium text-blue-600">Fetching HAWB…</span>
                                    ) : null}
                                  </div>
                                ) : line.hawb ? (
                                  <CopyableValue value={line.hawb} />
                                ) : (
                                  <span className="text-slate-400 font-medium">—</span>
                                )}
                              </td>
                            ) : null}

                            {/* Company name for custom batches; fulfillment status otherwise (Type) */}
                            <td className={`${tableCellClassName} w-36 whitespace-nowrap`}>
                              {customBatch ? (
                                <span className="text-sm font-medium text-slate-700">
                                  {line.companyName || '—'}
                                </span>
                              ) : linesEditable ? (
                                <select
                                  disabled={isFieldLocked(batch, line, stockVal)}
                                  className={`border border-slate-200 rounded px-2 py-1 text-sm font-medium text-slate-700 w-32 focus:border-blue-500 focus:outline-none bg-white/70 hover:bg-slate-50 transition-colors ${(isFieldLocked(batch, line, stockVal)) ? 'opacity-65 bg-slate-50 cursor-not-allowed' : ''
                                    }`}
                                  value={getUIStatus(line.inventoryStatus)}
                                  onChange={(e) => {
                                    handleInlineChange(batch.id, line.id, 'inventoryStatus', e.target.value);
                                  }}
                                  onBlur={() => {
                                      if (batch.id === inlineEditingBatchId) return;
                                      scheduleInlineSave(batch.id);
                                    }}
                                >
                                  <option value="Amazon">Amazon</option>
                                  <option value="Inventory" disabled={stockVal <= 0}>
                                    Inventory {stockVal <= 0 ? '(No Stock)' : ''}
                                  </option>
                                  <option value="Customer Service">Customer Service</option>
                                  <option value="Not feasible">Not feasible</option>
                                </select>
                              ) : (
                                <span className="text-sm font-medium text-slate-700">
                                  {getUIStatus(line.inventoryStatus)}
                                </span>
                              )}
                            </td>

                            {/* Status (Order status) */}
                            <td className={`${tableCellClassName} w-28 whitespace-nowrap`}>
                              {displayOrderStatus !== '—' ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                                  <span
                                    className={`h-2 w-2 rounded-full ${
                                      displayOrderStatus === 'shipped' ? 'bg-emerald-500' : 'bg-amber-500'
                                    }`}
                                  />
                                  <span
                                    className={
                                      displayOrderStatus === 'shipped' ? 'text-emerald-800' : 'text-amber-800'
                                    }
                                  >
                                    {displayOrderStatus.charAt(0).toUpperCase() + displayOrderStatus.slice(1)}
                                  </span>
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>

                            {/* Remarks */}
                            <td className={`${tableCellClassName} min-w-[180px] whitespace-nowrap`}>
                              {linesEditable ? (
                                <div className="flex items-start gap-2">
                                  <input
                                    type="text"
                                    placeholder="Remarks"
                                    disabled={isFieldLocked(batch, line, stockVal)}
                                    className={`border border-slate-200 rounded px-2 py-1 text-sm text-slate-700 w-full min-w-[150px] focus:border-blue-500 focus:outline-none bg-white/70 hover:bg-slate-50 focus:bg-white transition-colors ${(isFieldLocked(batch, line, stockVal)) ? 'opacity-65 bg-slate-50 cursor-not-allowed' : ''
                                      }`}
                                    value={line.supplierNotes || ''}
                                    onChange={(e) => handleInlineChange(batch.id, line.id, 'supplierNotes', e.target.value)}
                                    onBlur={() => {
                                      if (batch.id === inlineEditingBatchId) return;
                                      scheduleInlineSave(batch.id);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') e.target.blur();
                                    }}
                                  />
                                  <RemarkHistoryButton
                                    lineId={line.id}
                                    orderId={line.orderItem?.order?.id}
                                    onClick={() =>
                                      setRemarkHistoryTarget({
                                        lineId: line.id,
                                        orderId: line.orderItem?.order?.id || null,
                                      })
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="flex items-start gap-2">
                                  <span className="min-w-0 flex-1">{line.supplierNotes || '—'}</span>
                                  <RemarkHistoryButton
                                    lineId={line.id}
                                    orderId={line.orderItem?.order?.id}
                                    onClick={() =>
                                      setRemarkHistoryTarget({
                                        lineId: line.id,
                                        orderId: line.orderItem?.order?.id || null,
                                      })
                                    }
                                  />
                                </div>
                              )}
                            </td>

                            {/* Product / Buy Link */}
                            <td className={`${tableCellClassName} w-28 text-center whitespace-nowrap`}>
                              {buyUrl ? (
                                <a
                                  href={buyUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium justify-center"
                                >
                                  {customBatch ? 'Open link' : asin ? buyMarket.domain : 'Open'}
                                  <svg className="h-3.5 w-3.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>

                            {/* Confirm status */}
                            <td className={`${tableCellClassName} w-32 text-center whitespace-nowrap`}>
                              {(line.confirmed && isLineConfirmed(line, stockVal)) ? (
                                <div
                                  className="cursor-pointer select-none inline-flex items-center justify-center gap-1.5 text-xs font-semibold mx-auto"
                                  onDoubleClick={() => handleUnconfirmLine(batch.id, line.id)}
                                  title="Double click to edit / unconfirm"
                                >
                                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                  <span className="text-emerald-800">Confirmed</span>
                                </div>
                              ) : (
                                linesEditable ? (
                                  <button
                                    type="button"
                                    className="min-h-8 px-3 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-semibold shadow-sm transition-all"
                                    onClick={() => handleConfirmLine(batch.id, line.id)}
                                  >
                                    Confirm
                                  </button>
                                ) : (
                                  <div className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold mx-auto">
                                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                                    <span className="text-amber-800">Pending</span>
                                  </div>
                                )
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50/90 border-t-2 border-slate-200">
                        <td
                          colSpan={colsBeforeCurrency}
                          className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          <span className="mr-4 normal-case tracking-normal text-slate-600">
                            Total qty{' '}
                            <span className="font-bold text-slate-900">{batchTotalBuyQty}</span>
                          </span>
                          Total batch price
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">
                          {batchBuyingTotalEntries.length > 1 ? 'Mixed' : ''}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-400">
                          {/* Buying Price (unit) */}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {batchBuyingTotalEntries.length ? (
                            <div className="grid gap-0.5">
                              {batchBuyingTotalEntries.map(([code, total]) => (
                                <span key={code} className="text-sm font-bold text-slate-900">
                                  {formatBuyMoney(total, code)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm font-bold text-slate-400">—</span>
                          )}
                        </td>
                        <td colSpan={Math.max(0, colsAfterBuyingPrice - 1)} className="px-4 py-3" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </article>
            );
          })}
          {batchPagination.total > 1 ? (
            <p className="px-0.5 text-xs text-slate-500">
              All {batchPagination.total.toLocaleString('en-IN')} matching batches are in the
              dropdown.
            </p>
          ) : null}
        </div>
      ) : (
        <EmptyState
          title="No procurement batches"
          description={
            batchDays !== 'all' || batchStatus
              ? 'No batches match this date range / status. Try widening filters.'
              : 'Select orders and send them to procurement, or add manual lines above.'
          }
        />
      )}
      <ConfirmShipmentModal
        isOpen={Boolean(confirmShipmentTarget)}
        onClose={() => setConfirmShipmentTarget(null)}
        token={token}
        orderId={confirmShipmentTarget?.orderId}
        marketplace={confirmShipmentTarget?.marketplace}
        trackingNumber={confirmShipmentTarget?.trackingNumber}
        amazonOrderId={confirmShipmentTarget?.amazonOrderId}
        initialShipDate={confirmShipmentTarget?.initialShipDate}
        onSuccess={(payload) =>
          handleConfirmShipmentSuccess(confirmShipmentTarget?.lineId, payload)
        }
      />
      <BulkConfirmShipmentModal
        isOpen={Boolean(bulkConfirmTargets?.length)}
        onClose={() => setBulkConfirmTargets(null)}
        token={token}
        marketplace={bulkConfirmTargets?.[0]?.marketplace || 'IN'}
        targets={bulkConfirmTargets || []}
        onComplete={(summary) => {
          handleBulkConfirmComplete(summary);
          if (summary?.succeeded?.length) {
            showStatusToast({
              type: summary.failed?.length ? 'warning' : 'success',
              message: `Confirmed ${summary.succeeded.length} on Amazon${
                summary.failed?.length ? `, ${summary.failed.length} failed` : ''
              }.`,
            });
          }
        }}
      />
      <RemarkHistoryModal
        isOpen={Boolean(remarkHistoryTarget?.lineId || remarkHistoryTarget?.orderId)}
        onClose={() => setRemarkHistoryTarget(null)}
        token={token}
        lineId={remarkHistoryTarget?.lineId}
        orderId={remarkHistoryTarget?.orderId}
      />
      <RenameBatchModal
        isOpen={Boolean(renameBatchTarget)}
        onClose={() => {
          if (!renamingBatch) setRenameBatchTarget(null);
        }}
        onConfirm={handleRenameBatchConfirm}
        currentName={
          renameBatchTarget?.name ||
          (renameBatchTarget?.id ? `Batch ${renameBatchTarget.id.slice(0, 8)}` : '')
        }
        submitting={renamingBatch}
      />
      <ClearBatchesModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        onConfirm={clearAllBatches}
      />
      <ConfirmNotFeasibleModal
        isOpen={confirmNotFeasibleLine !== null}
        onClose={() => setConfirmNotFeasibleLine(null)}
        onConfirm={() => {
          if (confirmNotFeasibleLine) {
            handleConfirmLine(confirmNotFeasibleLine.batchId, confirmNotFeasibleLine.lineId, true);
          }
          setConfirmNotFeasibleLine(null);
        }}
      />
      <AlertModal
        isOpen={alertInfo.isOpen}
        onClose={() => setAlertInfo({ ...alertInfo, isOpen: false })}
        title={alertInfo.title}
        message={alertInfo.message}
        tone={alertInfo.tone || 'warning'}
      />
      <ConfirmDialog
        open={cancelBatchId !== null}
        title="Cancel Procurement Batch"
        message="Cancel this procurement batch? This cannot be undone."
        confirmLabel="Cancel Batch"
        cancelLabel="Keep Batch"
        tone="danger"
        onConfirm={handleCancelBatchConfirm}
        onCancel={() => setCancelBatchId(null)}
      />
    </section>
  );
}

function ConfirmNotFeasibleModal({ isOpen, onClose, onConfirm }) {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirmed = inputValue.trim().toLowerCase() === 'i confirm';

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="crm-panel w-full max-w-md p-6 shadow-2xl bg-white border border-slate-200 rounded-xl relative overflow-hidden"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-500" />

        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2.5">
          <svg className="h-5 w-5 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Confirm Not Feasible Order
        </h2>

        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
          This order is marked as <strong>Not feasible</strong> (unprofitable trade). Are you sure you want to confirm this line?
        </p>

        <div className="mt-5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Type "i confirm" to confirm
          </label>
          <input
            type="text"
            className="crm-input w-full border border-slate-200 rounded-md px-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 transition-all font-medium text-slate-800"
            placeholder="i confirm"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="crm-btn-secondary min-h-10 px-4 rounded-md text-sm font-semibold transition hover:bg-slate-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isConfirmed}
            className={`min-h-10 px-4 rounded-md text-sm font-semibold text-white transition-all duration-200 ${isConfirmed
              ? 'bg-amber-500 hover:bg-amber-600 cursor-pointer shadow-md shadow-amber-500/10'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function ClearBatchesModal({ isOpen, onClose, onConfirm }) {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (isOpen) {
      setInputValue('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirmed = inputValue.trim().toLowerCase() === 'i agree';

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="crm-panel w-full max-w-md p-6 shadow-2xl bg-white border border-slate-200 rounded-xl relative overflow-hidden"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-red-500" />

        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Delete All Batches
        </h2>

        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
          This will result in deletion of all procurement batches. <strong>This action cannot be undone.</strong>
        </p>

        <div className="mt-5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            Type "I agree" to confirm deletion
          </label>
          <input
            type="text"
            className="crm-input w-full border border-slate-200 rounded-md px-3 py-2 text-sm outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition-all font-medium text-slate-800"
            placeholder="I agree"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
          />
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="crm-btn-secondary min-h-10 px-4 rounded-md text-sm font-semibold transition hover:bg-slate-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isConfirmed}
            className={`min-h-10 px-4 rounded-md text-sm font-semibold text-white transition-all duration-200 ${isConfirmed
              ? 'bg-red-600 hover:bg-red-700 cursor-pointer shadow-md shadow-red-500/10'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            onClick={onConfirm}
          >
            Delete all batches
          </button>
        </div>
      </div>
    </div>
  );
}

const getUIStatus = (status) => {
  if (!status || status === 'procurement_required') return 'Amazon';
  return status;
};

const isCustomBatch = (batch) =>
  Boolean(batch?.lines?.length) && batch.lines.every((line) => !line.orderItemId);

/** Group key for same buy product across different customer orders (SKU first, else ASIN). */
function getProcurementProductKey(line) {
  const sku = String(line?.orderItem?.sku || line?.sku || '')
    .trim()
    .toUpperCase();
  if (sku) return `sku:${sku}`;

  const asin = String(
    line?.orderItem?.asinIn || line?.orderItem?.asinCom || line?.asin || ''
  )
    .trim()
    .toUpperCase();
  if (asin) return `asin:${asin}`;

  return `line:${line?.id || Math.random()}`;
}

function toMoneyNumber(value) {
  if (value === '' || value == null) return 0;
  const parsed =
    typeof value === 'number' ? value : Number(String(value).replace(/[$,]/g, '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

function getProcurementLineQty(line) {
  const buy = Number(line?.buyQuantity);
  if (Number.isFinite(buy) && buy > 0) return Math.floor(buy);
  const ordered = Number(line?.orderItem?.quantityOrdered || line?.quantity || 1);
  const extra = Number(line?.extraQuantity || 0);
  return (Number.isFinite(ordered) ? ordered : 1) + (Number.isFinite(extra) ? extra : 0);
}

function getLinePurchaseTotal(line) {
  const qty = getProcurementLineQty(line);
  const unit = Number(line?.purchasePrice);
  if (Number.isFinite(unit) && unit > 0 && qty > 0) {
    return Math.round(unit * qty * 100) / 100;
  }
  const stored = Number(line?.purchaseTotal);
  if (Number.isFinite(stored) && stored > 0) return stored;
  return null;
}

/** Split a group buy total onto lines (saved as buyQuantity for Amazon shipped tally). */
function distributeBuyQuantityAcrossLines(lines, totalBuy) {
  const list = lines || [];
  if (!list.length) return [];
  const target = Math.max(0, Math.floor(Number(totalBuy) || 0));
  const floors = list.map((line) =>
    Math.max(1, Number(line?.orderItem?.quantityOrdered || line?.quantity || 1) || 1)
  );
  const floorSum = floors.reduce((sum, qty) => sum + qty, 0);
  const shares = floors.map(() => 0);

  if (target >= floorSum) {
    for (let i = 0; i < list.length; i += 1) shares[i] = floors[i];
    shares[0] += target - floorSum;
  } else {
    let remaining = target;
    for (let i = 0; i < list.length; i += 1) {
      const take = Math.min(floors[i], remaining);
      shares[i] = take;
      remaining -= take;
    }
  }

  return list.map((line, index) => {
    const buyQuantity = shares[index];
    const ordered = floors[index];
    const next = {
      ...line,
      quantity: ordered,
      extraQuantity: Math.max(0, buyQuantity - ordered),
      buyQuantity,
      confirmed: false,
    };
    next.purchaseTotal = getLinePurchaseTotal(next);
    return next;
  });
}

function getProcurementProductTitle(line) {
  return (
    line?.orderItem?.title ||
    line?.title ||
    line?.orderItem?.sku ||
    line?.sku ||
    line?.orderItem?.asinIn ||
    line?.asin ||
    'Product'
  );
}

/** Group lines by SKU/ASIN; singleton groups keep a single line (no header needed). */
function groupProcurementLinesByProduct(lines) {
  const order = [];
  const map = new Map();

  for (const line of lines || []) {
    const key = getProcurementProductKey(line);
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key).push(line);
  }

  return order.map((key) => {
    const groupLines = map.get(key) || [];
    return {
      key,
      lines: groupLines,
      totalQty: groupLines.reduce((sum, line) => sum + getProcurementLineQty(line), 0),
      title: getProcurementProductTitle(groupLines[0]),
      sku: groupLines[0]?.orderItem?.sku || groupLines[0]?.sku || '',
      asin:
        groupLines[0]?.orderItem?.asinIn ||
        groupLines[0]?.orderItem?.asinCom ||
        groupLines[0]?.asin ||
        '',
    };
  });
}

/**
 * Flatten for table render.
 * product view: always a group header (shared buy fields) + child order lines when expanded.
 * Groups are collapsed by default (expandedProductKeys empty).
 */
function buildProcurementTableEntries(lines, viewMode, expandedProductKeys) {
  const list = lines || [];
  if (viewMode !== 'product') {
    return list.map((line) => ({ type: 'line', line, indented: false }));
  }

  const entries = [];
  for (const group of groupProcurementLinesByProduct(list)) {
    entries.push({ type: 'group', group });
    if (expandedProductKeys?.has(group.key)) {
      for (const line of group.lines) {
        entries.push({
          type: 'line',
          line,
          indented: true,
          groupKey: group.key,
        });
      }
    }
  }
  return entries;
}

/** Build API payload for a procurement line. Custom lines never send Extra Qty or seller-listing IDs as inventory IDs. */
const toProcurementLinePayload = (line) => {
  const isCustom = !line.orderItemId;
  const sku = line.orderItem?.sku || line.sku || undefined;
  const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin || undefined;
  const title = line.orderItem?.title || line.title || undefined;

  return {
    id: line.id,
    orderItemId: line.orderItemId || undefined,
    // Only pass a real inventory item id; selectedProductId is a seller listing id.
    inventoryItemId: isCustom ? undefined : (line.inventoryItemId || undefined),
    companyName: isCustom ? line.companyName?.trim() || '' : undefined,
    productLink: line.productLink || '',
    quantity: line.quantity,
    extraQuantity: isCustom ? 0 : (line.extraQuantity || 0),
    buyQuantity: (() => {
      const qty = Number(line.quantity || 0);
      const extra = isCustom ? 0 : Number(line.extraQuantity || 0);
      const explicit = line.buyQuantity != null ? Number(line.buyQuantity) : null;
      if (explicit != null && Number.isFinite(explicit) && explicit > 0) return Math.floor(explicit);
      return qty + extra;
    })(),
    supplierNotes: line.supplierNotes || '',
    sku: isCustom ? sku : undefined,
    asin: isCustom ? asin : undefined,
    title: isCustom ? title : undefined,
    usOrderId: line.usOrderId || undefined,
    usTrackingId: line.usTrackingId || undefined,
    hawb: line.hawb || undefined,
    sellingPrice: toMoneyNumber(line.sellingPrice),
    purchasePrice: toMoneyNumber(line.purchasePrice),
    purchaseTotal: (() => {
      const explicit = line.purchaseTotal != null ? Number(line.purchaseTotal) : null;
      if (explicit != null && Number.isFinite(explicit) && explicit > 0) {
        return Math.round(explicit * 100) / 100;
      }
      return getLinePurchaseTotal(line);
    })(),
    buyMarketplace: line.buyMarketplace || 'US',
    buyCarrierName: line.buyCarrierName || undefined,
    inventoryStatus: getUIStatus(line.inventoryStatus),
    confirmed: line.confirmed || false,
    isReorder: Boolean(line.isReorder),
  };
};

const applyStatusSideEffects = (line, field, value, inventoryList) => {
  if (field !== 'inventoryStatus') return { ...line, [field]: value };

  let updatedLine = { ...line, [field]: value };
  const statusVal = getUIStatus(value);
  const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
  const sku = line.orderItem?.sku || line.sku;

  if (statusVal === 'Inventory') {
    const invItem = inventoryList.find(
      (item) =>
        (sku && item.sku === sku) ||
        (asin && (item.asin === asin || item.asin === line.orderItem?.asinCom || item.asin === line.orderItem?.asinIn))
    );
    const invUnitCost = invItem ? invItem.unitCost : 0;
    updatedLine.usOrderId = '';
    updatedLine.usTrackingId = '';
    updatedLine.buyCarrierName = '';
    updatedLine.purchasePrice = invUnitCost;
  } else if (statusVal === 'Not feasible') {
    updatedLine.usOrderId = '';
    updatedLine.usTrackingId = '';
    updatedLine.buyCarrierName = '';
    updatedLine.purchasePrice = 0;
  } else if (statusVal === 'Customer Service') {
    updatedLine.usOrderId = '';
    updatedLine.usTrackingId = '';
    updatedLine.buyCarrierName = '';
    updatedLine.purchasePrice = 0;
  }

  return updatedLine;
};

function isLineConfirmed(line, stockVal) {
  const statusVal = getUIStatus(line.inventoryStatus);
  if (statusVal === 'Amazon') {
    const hasUsOrder = !!line.usOrderId?.trim();
    const hasBuyingPrice = line.purchasePrice !== undefined && line.purchasePrice !== null && line.purchasePrice !== '' && Number(line.purchasePrice) > 0;
    return hasUsOrder && hasBuyingPrice;
  }
  if (statusVal === 'Inventory') {
    return stockVal > 0;
  }
  if (statusVal === 'Customer Service' || statusVal === 'Not feasible') {
    return !!line.supplierNotes?.trim();
  }
  return false;
}

/** Full buy details filled (Amazon: order + tracking + price). Ready to confirm ≠ confirmed. */
function hasLineProcurementDetails(line, stockVal) {
  const statusVal = getUIStatus(line.inventoryStatus);
  if (statusVal === 'Amazon') {
    const hasUsOrder = !!String(line.usOrderId || '').trim();
    const hasUsTracking = !!String(line.usTrackingId || '').trim();
    const hasBuyingPrice =
      line.purchasePrice !== undefined &&
      line.purchasePrice !== null &&
      line.purchasePrice !== '' &&
      Number(line.purchasePrice) > 0;
    return hasUsOrder && hasUsTracking && hasBuyingPrice;
  }
  return isLineConfirmed(line, stockVal);
}

function getLineStockForFill(line, inventoryList) {
  const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
  const sku = line.orderItem?.sku || line.sku;
  const invItem = (inventoryList || []).find(
    (item) =>
      (sku && item.sku === sku) ||
      (asin &&
        (item.asin === asin ||
          item.asin === line.orderItem?.asinCom ||
          item.asin === line.orderItem?.asinIn))
  );
  return invItem ? invItem.quantityOnHand : line.orderItem?.quantityOnHand || 0;
}

/** incomplete | ready (ok, not confirmed) | confirmed */
function getLineFillState(line, inventoryList) {
  const stockVal = getLineStockForFill(line, inventoryList);
  if (line.confirmed) return 'confirmed';
  if (hasLineProcurementDetails(line, stockVal)) return 'ready';
  return 'incomplete';
}

function getBatchFillSummary(batch, inventoryList) {
  const lines = batch?.lines || [];
  const summary = { incomplete: 0, ready: 0, confirmed: 0, total: lines.length };
  for (const line of lines) {
    const state = getLineFillState(line, inventoryList);
    summary[state] += 1;
  }
  return summary;
}

function formatBatchFillLabel(summary) {
  const total = Number(summary?.total || 0);
  if (!total) return 'No lines';
  const parts = [];
  if (summary.incomplete) parts.push(`Incomplete ${summary.incomplete}`);
  if (summary.ready) parts.push(`Ok not confirmed ${summary.ready}`);
  if (summary.confirmed) parts.push(`Confirmed ${summary.confirmed}`);
  return parts.join(' · ') || `Incomplete ${total}`;
}

/** Prefer API fill; else compute from lines; else use lineCount so dropdown never shows false “No lines”. */
function resolveBatchOptionFill(batch, inventoryList = []) {
  const lines = batch?.lines || [];
  const lineCount = Number(batch?.lineCount ?? lines.length ?? 0);
  if (batch?.fill && Number(batch.fill.total) > 0) {
    return {
      lineCount: Number(batch.fill.total) || lineCount,
      fill: batch.fill,
    };
  }
  if (lines.length) {
    const fill = getBatchFillSummary(batch, inventoryList);
    return { lineCount: fill.total || lines.length, fill };
  }
  if (lineCount > 0) {
    return {
      lineCount,
      fill: { incomplete: lineCount, ready: 0, confirmed: 0, total: lineCount },
    };
  }
  return {
    lineCount: 0,
    fill: batch?.fill || { incomplete: 0, ready: 0, confirmed: 0, total: 0 },
  };
}

function normalizeBatchOption(batch, inventoryList = []) {
  const resolved = resolveBatchOptionFill(batch, inventoryList);
  return {
    ...batch,
    lineCount: resolved.lineCount,
    fill: resolved.fill,
  };
}

function AlertModal({ isOpen, onClose, title, message, tone = 'warning' }) {
  if (!isOpen) return null;

  const isSuccess = tone === 'success';
  const iconWrap = isSuccess
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-amber-100 text-amber-700';
  const buttonClass = isSuccess
    ? 'min-h-10 rounded-lg bg-emerald-600 px-5 text-sm font-semibold text-white transition hover:bg-emerald-700'
    : 'min-h-10 rounded-lg bg-slate-900 px-5 text-sm font-semibold text-white transition hover:bg-slate-800';

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="procurement-alert-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start gap-3">
            <span
              className={`inline-grid h-9 w-9 shrink-0 place-items-center rounded-full ${iconWrap}`}
              aria-hidden="true"
            >
              {isSuccess ? (
                <svg className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.25}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              )}
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 id="procurement-alert-title" className="text-[15px] font-semibold text-slate-900">
                {title}
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{message}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t border-slate-100 bg-slate-50 px-5 py-3">
          <button type="button" className={buttonClass} onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProcurementPage;
